<template>
  <Teleport :to="teleportTarget">
    <div
      class="fcw"
      :class="{ 'fcw--minimized': win.minimized, 'fcw--pipped': isPipped }"
      :style="isPipped
        ? { width: '100%', height: '100%' }
        : { left: win.x+'px', top: win.y+'px', width: win.w+'px', zIndex: win.z,
            height: win.minimized ? 'auto' : win.h+'px' }"
      @mousedown.capture="onWinClick"
    >
      <!-- ── Titlebar (drag) ── -->
      <div class="fcw-head" @mousedown.prevent="startDrag">
        <div class="fcw-av-wrap">
          <div class="fcw-av" :style="{ background: avatarColor(win.conv.contact.name) }">
            <img v-if="win.conv.contact.thumbnail" :src="win.conv.contact.thumbnail"
                 @error="(e) => (e.target as HTMLImageElement).style.display='none'" />
            <span v-else>{{ initials(win.conv.contact.name) }}</span>
          </div>
          <div v-if="mw.conv.status === 'snoozed'" class="fcw-av-snooze">
            <svg width="7" height="7" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
            </svg>
          </div>
        </div>
        <div class="fcw-head-info">
          <div class="fcw-head-name">{{ win.conv.contact.name }}</div>
          <div v-if="fcwDocument" class="fcw-head-doc">{{ fcwDocument.label }}: {{ fcwDocument.value }}</div>
          <div class="fcw-head-sub">
            <span v-if="win.conv.team">{{ win.conv.team.name }} · </span>#{{ win.conv.id }}
            <span :class="['fcw-status-lbl', mw.conv.status]">{{ convStatusLabel(mw.conv.status) }}</span>
            <span v-if="slaBadge" :class="['fcw-sla-badge', slaBadge.status]">
              <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
              </svg>
              <strong>{{ slaBadge.time }}</strong>
              <span>{{ slaBadge.label }}</span>
            </span>
            <span v-if="mw.conv.assignee" class="fcw-agent-tag">
              <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
              </svg>
              {{ mw.conv.assignee.name }}
            </span>
          </div>
        </div>
        <div class="fcw-head-btns" @mousedown.stop>
          <!-- Status dropdown -->
          <div class="fcw-status-wrap" @click.stop>
            <button class="fcw-btn" :title="`Status: ${convStatusLabel(mw.conv.status)}`"
                    @click="!isReadOnly && (statusMenuOpen = !statusMenuOpen, snoozeMode = false)">
              <span class="fcw-s-dot" :class="mw.conv.status"></span>
            </button>
            <div v-if="statusMenuOpen" class="fcw-status-menu">
              <template v-if="!snoozeMode">
                <button v-if="mw.conv.status !== 'open'"     class="fcw-sopt" @click="doChangeStatus('open')">
                  <span class="fcw-s-dot open"></span> Abrir
                </button>
                <button v-if="mw.conv.status !== 'resolved'" class="fcw-sopt" @click="doChangeStatus('resolved')">
                  <span class="fcw-s-dot resolved"></span> Resolver
                </button>
                <button v-if="mw.conv.status !== 'pending'"  class="fcw-sopt" @click="doChangeStatus('pending')">
                  <span class="fcw-s-dot pending"></span> Pendente
                </button>
                <button class="fcw-sopt" @click="snoozeMode = true">
                  <span class="fcw-s-dot snoozed"></span> Adiar…
                </button>
              </template>
              <template v-else>
                <div class="fcw-snz">
                  <div class="fcw-snz-title">Adiar por quanto tempo?</div>
                  <div class="fcw-snz-row">
                    <input type="number" v-model.number="snoozeH" min="0" max="99" class="fcw-snz-inp" />
                    <span class="fcw-snz-lbl">h</span>
                    <input type="number" v-model.number="snoozeM" min="0" max="59" class="fcw-snz-inp" />
                    <span class="fcw-snz-lbl">min</span>
                  </div>
                  <div class="fcw-snz-hint">0h 0min = até próxima resposta</div>
                  <div class="fcw-snz-btns">
                    <button class="fcw-snz-cancel"  @click="snoozeMode = false">Cancelar</button>
                    <button class="fcw-snz-confirm" @click="confirmSnooze" :disabled="statusLoading">Confirmar</button>
                  </div>
                </div>
              </template>
            </div>
          </div>
          <button class="fcw-btn" :class="{ 'fcw-btn--active': macroPanelOpen }" @click="toggleMacroPanel" title="Macros">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <polygon points="5 3 19 12 5 21 5 3"/>
            </svg>
          </button>
          <button class="fcw-btn" @click="openMsgSearch" title="Buscar nas mensagens">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
          </button>
          <button v-if="pipSupported && !isPipped" class="fcw-btn" @click="popOut" title="Abrir em janela flutuante do sistema (fora do navegador)">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
              <polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
            </svg>
          </button>
          <button v-if="isPipped" class="fcw-btn" @click="() => pipWin?.close()" title="Voltar pra página">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V9m0 0l6 6m-6-6h6V3"/>
            </svg>
          </button>
          <button v-if="!isPipped" class="fcw-btn" @click="toggleMinimize" :title="win.minimized ? 'Restaurar' : 'Minimizar'">
            <svg v-if="win.minimized" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="18 15 12 9 6 15"/>
            </svg>
            <svg v-else width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
          </button>
          <button class="fcw-btn fcw-btn-close" @click="$emit('close')" title="Fechar">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
      </div>

      <!-- ── Conteúdo (oculto quando minimizado) ── -->
      <template v-if="!win.minimized">
        <!-- Etiquetas + Prioridade -->
        <div class="fcw-lbls">

          <!-- Atribuição de agente -->
          <div v-if="!isReadOnly" class="fcw-agent-wrap" @click.stop>
            <span v-if="mw.conv.assignee" class="fcw-agent-sel" @click.stop="agentMenuOpen = !agentMenuOpen" title="Mudar agente">
              <svg width="7" height="7" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
              {{ mw.conv.assignee.name }}
            </span>
            <button v-else class="fcw-lbl-add" @click.stop="agentMenuOpen = !agentMenuOpen">
              <svg width="7" height="7" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
              agente
            </button>
            <div v-if="agentMenuOpen" class="fcw-agent-menu">
              <div class="fcw-agent-search-wrap">
                <input v-model="agentSearch" class="fcw-agent-search" placeholder="Buscar agente…" @click.stop @keydown.stop />
              </div>
              <div v-if="agentLoading" style="padding:12px;display:flex;justify-content:center">
                <span class="fcw-spinner" style="width:14px;height:14px;border-width:2px"></span>
              </div>
              <template v-else>
                <div class="fcw-agent-list">
                  <button class="fcw-agent-opt" :class="{ active: !mw.conv.assignee }" @click.stop="doAssignAgent(null)">
                    <div class="fcw-agent-av" style="background:var(--ink-10);color:var(--ink-50);font-size:10px">—</div>
                    Sem agente
                    <svg v-if="!mw.conv.assignee" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" style="margin-left:auto;color:#22c55e;flex-shrink:0"><polyline points="20 6 9 17 4 12"/></svg>
                  </button>
                  <button v-for="a in filteredAgents" :key="a.id" class="fcw-agent-opt" :class="{ active: mw.conv.assignee?.id === a.id }" @click.stop="doAssignAgent(a.id)">
                    <div class="fcw-agent-av-wrap" :title="availLabel(agentStatusOf(a.id))">
                      <div class="fcw-agent-av" :style="{ background: avatarColor(a.name), overflow:'hidden' }">
                        <img v-if="a.thumbnail" :src="a.thumbnail" :alt="a.name" style="width:100%;height:100%;object-fit:cover;border-radius:50%;display:block" @error="(e) => (e.target as HTMLImageElement).style.display='none'" />
                        <span v-else>{{ initials(a.name) }}</span>
                      </div>
                      <span class="fcw-agent-status-dot" :class="`fasd-${agentStatusOf(a.id)}`"></span>
                    </div>
                    {{ a.name }}
                    <svg v-if="mw.conv.assignee?.id === a.id" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" style="margin-left:auto;color:#22c55e;flex-shrink:0"><polyline points="20 6 9 17 4 12"/></svg>
                  </button>
                </div>
              </template>
            </div>
          </div>

          <!-- Atribuição de time -->
          <div v-if="!isReadOnly && canAssignTeam" class="fcw-agent-wrap" @click.stop>
            <span v-if="mw.conv.team" class="fcw-agent-sel" @click.stop="teamMenuOpen = !teamMenuOpen" title="Mudar time">
              <svg width="7" height="7" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
              {{ mw.conv.team.name }}
            </span>
            <button v-else class="fcw-lbl-add" @click.stop="teamMenuOpen = !teamMenuOpen">
              <svg width="7" height="7" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
              time
            </button>
            <div v-if="teamMenuOpen" class="fcw-agent-menu">
              <div class="fcw-agent-search-wrap">
                <input v-model="teamSearch" class="fcw-agent-search" placeholder="Buscar time…" @click.stop @keydown.stop />
              </div>
              <div v-if="teamsLoading" style="padding:12px;display:flex;justify-content:center">
                <span class="fcw-spinner" style="width:14px;height:14px;border-width:2px"></span>
              </div>
              <template v-else>
                <div class="fcw-agent-list">
                  <button class="fcw-agent-opt" :class="{ active: !mw.conv.team }" @click.stop="doAssignTeam(null)">
                    <div class="fcw-agent-av" style="background:var(--ink-10);color:var(--ink-50);font-size:10px">—</div>
                    Sem time
                    <svg v-if="!mw.conv.team" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" style="margin-left:auto;color:#22c55e;flex-shrink:0"><polyline points="20 6 9 17 4 12"/></svg>
                  </button>
                  <button v-for="t in filteredTeams" :key="t.id" class="fcw-agent-opt" :class="{ active: mw.conv.team?.id === t.id }" @click.stop="doAssignTeam(t.id)">
                    <div class="fcw-agent-av" :style="{ background: avatarColor(t.name) }">
                      {{ initials(t.name) }}
                    </div>
                    {{ t.name }}
                    <svg v-if="mw.conv.team?.id === t.id" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" style="margin-left:auto;color:#22c55e;flex-shrink:0"><polyline points="20 6 9 17 4 12"/></svg>
                  </button>
                </div>
              </template>
            </div>
          </div>

          <!-- Tag de prioridade ativa -->
          <div v-if="!isReadOnly" class="fcw-priority-wrap" @click.stop>
            <span
              v-if="currentPriority"
              class="fcw-priority-tag"
              :style="{ background: PRIORITY_COLORS[currentPriority] + '22', color: PRIORITY_COLORS[currentPriority], borderColor: PRIORITY_COLORS[currentPriority] + '55' }"
              @click.stop="priorityMenuOpen = !priorityMenuOpen"
              :title="`Prioridade: ${PRIORITY_LABELS[currentPriority]}`"
            >
              <svg width="7" height="7" viewBox="0 0 24 24" fill="currentColor" style="flex-shrink:0">
                <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"/>
              </svg>
              {{ PRIORITY_LABELS[currentPriority] }}
              <span v-if="priorityLoading" class="fcw-p-spin"></span>
            </span>
            <button
              v-else
              class="fcw-lbl-add fcw-priority-add"
              @click.stop="priorityMenuOpen = !priorityMenuOpen"
              :disabled="priorityLoading"
            >
              <svg v-if="!priorityLoading" width="7" height="7" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"/>
              </svg>
              <span v-else class="fcw-p-spin"></span>
              prioridade
            </button>
            <div v-if="priorityMenuOpen" class="fcw-priority-menu">
              <button
                v-for="p in (['urgent','high','medium','low','none'] as const)"
                :key="p"
                class="fcw-priority-opt"
                :class="{ active: mw.conv.priority === p || (!mw.conv.priority && p === 'none') }"
                @click.stop="doTogglePriority(p)"
              >
                <span v-if="p !== 'none'" class="fcw-priority-dot" :style="{ background: PRIORITY_COLORS[p] }"></span>
                <svg v-else width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12"/></svg>
                {{ PRIORITY_LABELS[p] }}
                <svg v-if="mw.conv.priority === p || (!mw.conv.priority && p === 'none')" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" style="margin-left:auto;color:#22c55e"><polyline points="20 6 9 17 4 12"/></svg>
              </button>
            </div>
          </div>

          <span v-for="l in currentLabels" :key="l" class="fcw-lbl">
            {{ l.replace(/_/g, ' ') }}
            <button v-if="!isReadOnly && !isLabelProtected(l)" class="fcw-lbl-x" @click.stop="toggleLabel(l)" :disabled="updatingLabels">×</button>
            <svg v-else-if="!isReadOnly" class="fcw-lbl-lock" width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" title="Etiqueta protegida — não pode ser removida">
              <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
          </span>
          <div v-if="!isReadOnly" class="fcw-lbl-picker">
            <button class="fcw-lbl-add" @click.stop="labelsOpen = !labelsOpen" :disabled="updatingLabels">
              <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round">
                <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
              etiqueta
            </button>
            <div v-if="labelsOpen" class="fcw-lbl-dropdown">
              <div v-if="!allLabels.length" class="fcw-lbl-loading">
                <span class="fcw-spinner" style="width:12px;height:12px;border-width:2px"></span>
              </div>
              <label v-for="lbl in allLabels" :key="lbl.id"
                     class="fcw-lbl-opt" :class="{ checked: currentLabels.includes(lbl.title) }"
                     @click.prevent="(currentLabels.includes(lbl.title) && isLabelProtected(lbl.title)) || toggleLabel(lbl.title)">
                <span class="fcw-lbl-dot" :style="{ background: lbl.color }"></span>
                <span>{{ lbl.title.replace(/_/g, ' ') }}</span>
                <svg v-if="currentLabels.includes(lbl.title) && isLabelProtected(lbl.title)" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" style="margin-left:auto;color:var(--ink-40)" title="Etiqueta protegida — não pode ser removida">
                  <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
                <svg v-else-if="currentLabels.includes(lbl.title)" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" style="margin-left:auto;color:#22c55e">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              </label>
            </div>
          </div>
        </div>

        <!-- Snooze banner -->
        <div v-if="mw.conv.status === 'snoozed'" class="fcw-snooze-banner">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
          </svg>
          <span v-if="snoozeRemainingLabel">Adiado — {{ snoozeRemainingLabel }} restantes</span>
          <span v-else>Adiado até próxima resposta do cliente</span>
        </div>

        <!-- Busca nas mensagens -->
        <div v-if="msgSearchOpen" class="fcw-msg-search-bar" @mousedown.stop>
          <input
            ref="msgSearchInputEl"
            v-model="msgSearchQuery"
            class="fcw-msg-search-input"
            placeholder="Buscar nas mensagens…"
            @keydown.enter="doMsgSearch"
            @keydown.escape="closeMsgSearch"
          />
          <span v-if="msgSearchActive" class="fcw-msg-search-count">
            {{ msgMatchIds.length ? (msgCurrentIdx + 1) + '/' + msgMatchIds.length : '0 resultados' }}
          </span>
          <button v-if="msgMatchIds.length" class="fcw-msg-search-nav" @click="goPrevMsg" title="Anterior">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="18 15 12 9 6 15"/></svg>
          </button>
          <button v-if="msgMatchIds.length" class="fcw-msg-search-nav" @click="goNextMsg" title="Próximo">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
          </button>
          <button class="fcw-msg-search-close" @click="closeMsgSearch">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        <!-- ── Painel de Macros ──────────────────────────── -->
        <div v-if="macroPanelOpen" class="fcw-macro-panel">

          <!-- Header do painel -->
          <div class="fcw-macro-head">
            <div class="fcw-macro-search-wrap">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input v-model="macroSearch" class="fcw-macro-search" placeholder="Buscar macro…" @click.stop @keydown.stop />
            </div>
            <span class="fcw-macro-count">{{ filteredMacros.length }}</span>
          </div>

          <!-- Estado: carregando -->
          <div v-if="macrosLoading" class="fcw-macro-loading">
            <span class="fcw-spinner" style="width:16px;height:16px;border-width:2px"></span>
          </div>

          <!-- Resultado de execução -->
          <div v-else-if="macroRunning || macroRunDone" class="fcw-macro-results">
            <div class="fcw-macro-results-title">
              <button class="fcw-macro-back" @click="resetMacroRun" :disabled="macroRunning">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
                Voltar
              </button>
              <span>{{ macroRunningObj?.name }}</span>
            </div>
            <div class="fcw-macro-steps">
              <div
                v-for="(r, i) in macroStepResults"
                :key="i"
                class="fcw-macro-step"
                :class="r.ok ? 'fcw-macro-step--ok' : 'fcw-macro-step--err'"
              >
                <span class="fcw-macro-step-icon">
                  <svg v-if="r.ok" width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  <svg v-else width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </span>
                <div class="fcw-macro-step-body">
                  <div class="fcw-macro-step-label">{{ r.label }}</div>
                  <div v-if="!r.ok" class="fcw-macro-step-err">{{ r.msg }}</div>
                </div>
              </div>
              <!-- Passo atual sendo executado -->
              <div v-if="macroRunning" class="fcw-macro-step fcw-macro-step--pending">
                <span class="fcw-macro-step-spin"></span>
                <div class="fcw-macro-step-body">
                  <div class="fcw-macro-step-label">
                    {{ macroRunningObj?.actions[macroStepResults.length]
                       ? (MACRO_ACTION_LABELS[macroRunningObj.actions[macroStepResults.length].action_name] ?? '…')
                       : '…' }}
                  </div>
                </div>
              </div>
            </div>
            <!-- Resumo final -->
            <div v-if="macroRunDone" class="fcw-macro-summary" :class="macroStepResults.every(r => r.ok) ? 'fcw-macro-summary--ok' : 'fcw-macro-summary--warn'">
              <svg v-if="macroStepResults.every(r => r.ok)" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
              <svg v-else width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
              <span v-if="macroStepResults.every(r => r.ok)">Concluído com sucesso!</span>
              <span v-else>{{ macroStepResults.filter(r => !r.ok).length }} ação(ões) com erro</span>
            </div>
          </div>

          <!-- Lista de macros -->
          <div v-else class="fcw-macro-list">
            <div v-if="!macrosList.length && !macrosLoading" class="fcw-macro-empty">
              Nenhuma macro encontrada.
            </div>
            <button
              v-for="m in filteredMacros"
              :key="m.id"
              class="fcw-macro-item"
              @click.stop="runMacroInChat(m)"
            >
              <div class="fcw-macro-item-info">
                <span class="fcw-macro-item-name">{{ m.name }}</span>
                <span class="fcw-macro-item-count">{{ m.actions.length }} ação{{ m.actions.length !== 1 ? 'ões' : '' }}</span>
              </div>
              <svg class="fcw-macro-item-run" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <polygon points="5 3 19 12 5 21 5 3"/>
              </svg>
            </button>
          </div>

        </div>

        <!-- Mensagens -->
        <div v-show="!macroPanelOpen" class="fcw-msgs" ref="msgsEl" @scroll="onMsgsScroll">
          <div v-if="loadingMsgs || msgSearchLoading" class="fcw-msgs-loading">
            <span class="fcw-spinner"></span>
            <span v-if="msgSearchLoading" class="fcw-msgs-loading-label">Buscando em toda a conversa…</span>
          </div>
          <template v-else>
            <div v-if="loadingMore" class="fcw-load-more-row">
              <span class="fcw-spinner-sm"></span>
            </div>
            <div v-else-if="noMoreMsgs" class="fcw-no-more">início da conversa</div>
            <div v-for="msg in processedMsgs" :key="msg.id"
                 class="fcw-msg-row"
                 :class="{ out: msg.message_type === 1, inn: msg.message_type === 0, act: msg.message_type === 2, 'msg-match-current': msgSearchActive && msgMatchIds[msgCurrentIdx] === msg.id }"
                 :data-msg-id="msg.id">
              <div v-if="msg.message_type === 2" class="fcw-msg-act">{{ msg.content }}</div>
              <template v-else>
                <div v-if="msg.showAvatar" class="fcw-msg-av"
                     :style="{ background: avatarColor(senderName(msg)) }" :title="senderName(msg)">
                  <img v-if="msg.sender?.thumbnail && msg.message_type === 1"
                       :src="msg.sender.thumbnail" @error="(e) => (e.target as HTMLImageElement).style.display='none'" />
                  <span v-else>{{ initials(senderName(msg)) }}</span>
                </div>
                <div v-else class="fcw-msg-av-spacer"></div>
                <div class="fcw-bubble">
                  <span v-if="msg.private" class="fcw-priv-tag">
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                    Nota interna
                  </span>
                  <span v-if="msg.content" class="fcw-bubble-text" :class="{ 'fcw-bubble-text--priv': msg.private }" v-html="highlightMsgHtml(msg.content, msg.message_type === 1, authUser?.message_signature)"></span>
                  <!-- Botão de download para mensagens de arquivo enviado via n8n -->
                  <a
                    v-if="msg.message_type === 1 && extractFileKey(msg.content || '')"
                    :href="n8nDownloadUrl(extractFileKey(msg.content || ''))"
                    target="_blank"
                    rel="noopener"
                    class="fcw-file-dl-btn"
                  >
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                    </svg>
                    Baixar arquivo
                  </a>
                  <template v-if="msg.attachments?.length">
                    <div v-for="att in msg.attachments" :key="att.id" class="fcw-att">
                      <a v-if="att.file_type === 'image'" :href="att.data_url" target="_blank" rel="noopener" class="fcw-att-img-wrap">
                        <img :src="att.thumb_url || att.data_url" class="fcw-att-img" loading="lazy" />
                      </a>
                      <div v-else-if="att.file_type === 'audio'" class="fcw-ap">
                        <button class="fcw-ap-btn" @click="toggleAudio(att)">
                          <svg v-if="audioState[att.id]?.playing" width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
                            <rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/>
                          </svg>
                          <svg v-else width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
                            <polygon points="5 3 19 12 5 21 5 3"/>
                          </svg>
                        </button>
                        <div class="fcw-ap-mid">
                          <div class="fcw-ap-bar" @click.stop>
                            <div class="fcw-ap-fill" :style="{ width: audioProgress(att.id)+'%' }"></div>
                            <input type="range" class="fcw-ap-seek" min="0" max="100" step="0.1"
                                   :value="audioProgress(att.id)" @input="seekAudio(att, $event)" />
                          </div>
                          <div class="fcw-ap-time">
                            <span>{{ fmtTime(audioState[att.id]?.current || 0) }}</span>
                            <span>{{ fmtTime(audioState[att.id]?.duration || 0) }}</span>
                          </div>
                        </div>
                        <a :href="att.data_url" target="_blank" rel="noopener" class="fcw-ap-dl" title="Baixar">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                            <polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                          </svg>
                        </a>
                      </div>
                      <div v-else-if="isVideo(att)" class="fcw-att-video-wrap">
                        <video :src="att.data_url" controls preload="metadata" class="fcw-att-video"></video>
                      </div>
                      <a v-else-if="isPdf(att)" :href="att.data_url" target="_blank" rel="noopener" class="fcw-att-file fcw-att-pdf">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                          <polyline points="14 2 14 8 20 8"/>
                        </svg>
                        <div class="fcw-att-info">
                          <span class="fcw-att-ext">PDF</span>
                          <span class="fcw-att-size">{{ fmtSize(att.file_size) }}</span>
                        </div>
                      </a>
                      <a v-else :href="att.data_url" target="_blank" rel="noopener" class="fcw-att-file">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                          <polyline points="14 2 14 8 20 8"/>
                        </svg>
                        <div class="fcw-att-info">
                          <span class="fcw-att-ext">{{ att.extension?.toUpperCase() || 'Arquivo' }}</span>
                          <span class="fcw-att-size">{{ fmtSize(att.file_size) }}</span>
                        </div>
                      </a>
                    </div>
                  </template>
                  <span class="fcw-bubble-ts">
                    {{ fmtTs(msg.created_at) }}
                    <span v-if="msg.message_type === 1" class="fcw-msg-status" :class="'s-'+(msg.status||'sent')" :title="msg.status === 'read' ? 'Lido' : msg.status === 'delivered' ? 'Entregue' : msg.status === 'failed' ? 'Falhou' : 'Enviado'">
                      <svg v-if="msg.status === 'failed'" width="8" height="8" viewBox="0 0 8 8" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><line x1="1" y1="1" x2="7" y2="7"/><line x1="7" y1="1" x2="1" y2="7"/></svg>
                      <svg v-else-if="msg.status === 'read' || msg.status === 'delivered'" width="14" height="8" viewBox="0 0 14 8" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><polyline points="1,4 3.5,6.5 9,1"/><polyline points="5,4 7.5,6.5 13,1"/></svg>
                      <svg v-else width="10" height="8" viewBox="0 0 10 8" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><polyline points="1,4 3.5,6.5 9,1"/></svg>
                    </span>
                  </span>
                </div>
              </template>
            </div>
          </template>
        </div>

        <!-- Banner somente leitura (conversa resolvida) -->
        <div v-if="isReadOnly && !macroPanelOpen" class="fcw-readonly-banner">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
          Conversa resolvida — somente leitura
        </div>

        <!-- Banner: janela de 24h de mensagens fechada -->
        <div v-if="isWindowClosed && !isReadOnly && !macroPanelOpen" class="fcw-window-banner">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          Você só pode responder usando um template — janela de 24h do WhatsApp encerrada
        </div>

        <!-- Preview de arquivos -->
        <div v-if="!isReadOnly && !macroPanelOpen && attachFiles.length" class="fcw-files">
          <div v-for="(f, i) in attachFiles" :key="i" class="fcw-file-pill">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
            </svg>
            <span>{{ f.name }}</span>
            <button @click="attachFiles.splice(i,1)">×</button>
          </div>
        </div>

        <!-- Compositor -->
        <div v-if="!isReadOnly && !macroPanelOpen && !recording && !recordedBlob" class="fcw-composer" :class="{ 'fcw-composer--priv': notaInterna }">
          <label class="fcw-attach" :class="{ 'fcw-attach-disabled': isWindowClosed || notaInterna }" title="Anexar">
            <input type="file" multiple hidden ref="fileInputEl" :disabled="isWindowClosed || notaInterna" @change="onFilePick" />
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
            </svg>
          </label>
          <button
            v-if="can('messages.privateNote')"
            class="fcw-priv-btn"
            :class="{ 'fcw-priv-btn--active': notaInterna }"
            :disabled="isWindowClosed"
            @click="notaInterna = !notaInterna"
            :title="notaInterna ? 'Nota interna ativada — clique para voltar ao modo normal' : 'Marcar como nota interna (não visível pro cliente)'"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
          </button>
          <EmojiPicker v-if="!isWindowClosed" @pick="insertEmoji" />
          <textarea ref="taEl" v-model="draft" class="fcw-ta"
                    :placeholder="notaInterna ? 'Nota interna — só a equipe vê… (Enter envia)' : (isWindowClosed ? 'Envie um template para responder…' : 'Mensagem… (Enter envia)')"
                    :disabled="isWindowClosed" rows="1"
                    @keydown.enter.exact.prevent="sendMsg" @input="growTa"></textarea>
          <template v-if="!notaInterna && (isWindowClosed || (!canSend && !sending))">
            <button class="fcw-wa-tpl" @click="openTplModal" title="Enviar template WhatsApp">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zm-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884zm8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
            </button>
            <button v-if="!isWindowClosed" class="fcw-mic" @click="startRecording" title="Gravar áudio">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
                <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                <line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/>
              </svg>
            </button>
          </template>
          <button v-else class="fcw-send" @click="sendMsg" :disabled="!canSend || sending">
            <span v-if="sending" class="fcw-spinner-sm-white"></span>
            <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
            </svg>
          </button>
        </div>

        <!-- Gravando áudio -->
        <div v-else-if="!isReadOnly && !macroPanelOpen && recording" class="fcw-recording">
          <button class="fcw-rec-cancel" @click="cancelRecording" title="Cancelar">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
          <span class="fcw-rec-dot"></span>
          <div class="fcw-rec-wave">
            <div v-for="(h, i) in waveformBars" :key="i" class="fcw-rec-bar" :style="{ height: h+'px' }"></div>
          </div>
          <span class="fcw-rec-time">{{ fmtRecTime(recordingTime) }}</span>
          <button class="fcw-rec-stop" @click="stopRecording" title="Parar">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
              <rect x="4" y="4" width="16" height="16" rx="2"/>
            </svg>
          </button>
        </div>

        <!-- Preview do áudio gravado -->
        <div v-else-if="!isReadOnly && !macroPanelOpen && recordedBlob" class="fcw-rec-preview">
          <button class="fcw-rec-cancel" @click="cancelRecording" title="Descartar">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
          <audio :src="recordedUrl" controls preload="auto" class="fcw-rec-audio"></audio>
          <button class="fcw-rec-send" @click="sendRecordedAudio" title="Enviar">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
            </svg>
          </button>
        </div>

        <!-- Handle de redimensionamento (não faz sentido pop-ado — o SO redimensiona a janela) -->
        <template v-if="!isPipped">
          <div class="fcw-resize-se" @mousedown.prevent="startResize('se')"></div>
          <div class="fcw-resize-s"  @mousedown.prevent="startResize('s')"></div>
          <div class="fcw-resize-e"  @mousedown.prevent="startResize('e')"></div>
          <div class="fcw-resize-w"  @mousedown.prevent="startResize('w')"></div>
        </template>
      </template>
    </div>
  </Teleport>

  <!-- ── Modal: Enviar Template WhatsApp ── -->
  <Teleport :to="teleportTarget">
    <div v-if="tplModalOpen" class="fcw-tpl-overlay" @mousedown.self="closeTplModal">
      <div class="fcw-tpl-dialog">

        <!-- Header -->
        <div class="fcw-tpl-head">
          <div class="fcw-tpl-title">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="#25D366"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zm-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884zm8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
            Enviar Template WhatsApp
            <span class="fcw-tpl-contact">→ {{ props.win.conv.contact.name }}</span>
          </div>
          <button class="fcw-tpl-close" @click="closeTplModal">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M18 6 6 18M6 6l12 12"/></svg>
          </button>
        </div>

        <!-- Body -->
        <div class="fcw-tpl-body">

          <!-- Coluna esquerda: lista de templates -->
          <div class="fcw-tpl-list-col">
            <div class="fcw-tpl-col-label">Templates aprovados</div>

            <div v-if="tplsLoading" class="fcw-tpl-loading">
              <span class="fcw-tpl-spin"></span> Carregando…
            </div>
            <div v-else-if="!tplsApproved.length" class="fcw-tpl-empty">Nenhum template aprovado</div>
            <div v-else class="fcw-tpl-items">
              <button
                v-for="t in tplsApproved" :key="t.id"
                class="fcw-tpl-item" :class="{ active: tplSelected?.id === t.id }"
                @click="selectTpl(t)"
              >
                <div class="fcw-tpl-item-name">{{ t.name.replace(/_/g,' ') }}</div>
                <div class="fcw-tpl-item-meta">
                  <span class="fcw-tpl-cat" :class="t.category.toLowerCase()">{{ t.category === 'MARKETING' ? 'Marketing' : t.category === 'UTILITY' ? 'Utilidade' : t.category }}</span>
                  <span class="fcw-tpl-lang">{{ t.language }}</span>
                </div>
              </button>
            </div>
          </div>

          <!-- Coluna direita: preview + variáveis -->
          <div class="fcw-tpl-config-col">
            <div v-if="!tplSelected" class="fcw-tpl-pick-hint">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/><rect x="9" y="3" width="6" height="4" rx="1"/></svg>
              <span>Selecione um template ao lado</span>
            </div>
            <template v-else>
              <!-- Preview -->
              <div class="fcw-tpl-col-label" style="margin-bottom:8px">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                Preview em tempo real
              </div>
              <div class="fcw-tpl-preview-wrap">
                <div class="fcw-tpl-bubble">
                  <div v-if="tplHasImg" class="fcw-tpl-bubble-img">
                    <img v-if="tplImgUrl && !tplImgErr" :src="tplImgUrl" alt="" @error="tplImgErr = true" />
                    <div v-else class="fcw-tpl-img-ph">
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>
                    </div>
                  </div>
                  <div v-if="tplHeaderTxt" class="fcw-tpl-bubble-hdr">{{ tplHeaderTxt }}</div>
                  <div class="fcw-tpl-bubble-body" v-html="tplPreviewHtml"></div>
                  <div v-if="tplFooterTxt" class="fcw-tpl-bubble-ftr">{{ tplFooterTxt }}</div>
                  <div class="fcw-tpl-bubble-time">{{ tplTime }}</div>
                </div>
                <div v-if="tplButtons.length" class="fcw-tpl-btns">
                  <div v-for="(b,i) in tplButtons" :key="i" class="fcw-tpl-btn">{{ b.text || 'Botão' }}</div>
                </div>
              </div>

              <!-- URL da imagem (se template tem imagem) -->
              <div v-if="tplHasImg" style="margin-top:10px">
                <div class="fcw-tpl-field-label">URL da imagem</div>
                <input type="url" class="fcw-tpl-input" v-model="tplImgUrl" placeholder="https://…" @input="tplImgErr = false" />
                <div class="fcw-tpl-hint">Use uma URL pública permanente</div>
              </div>

              <!-- Variáveis -->
              <div v-if="tplVarNums.length" style="margin-top:12px">
                <div class="fcw-tpl-col-label" style="margin-bottom:6px">Variáveis</div>
                <div v-for="n in tplVarNums" :key="n" style="margin-bottom:7px">
                  <div class="fcw-tpl-field-label">Variável &#123;&#123;{{ n }}&#125;&#125;</div>
                  <div v-if="tplVarMapping[n]" class="fcw-tpl-mapped-badge">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
                    Preenchido automaticamente: {{ mappedLabel(n) }}
                  </div>
                  <input v-else type="text" class="fcw-tpl-input" :placeholder="`Valor para {{${n}}}`" :value="tplVars[n] || ''" @input="setTplVar(n, ($event.target as HTMLInputElement).value)" />
                </div>
              </div>

              <!-- Botão enviar -->
              <div style="margin-top:16px">
                <div v-if="tplSendBlocked" class="fcw-tpl-blocked-hint">{{ tplSendBlockedReason }}</div>
                <button
                  class="fcw-tpl-send-btn"
                  :disabled="tplSending || tplSendBlocked"
                  :title="tplSendBlockedReason"
                  @click="sendTplToContact"
                >
                  <span v-if="!tplSending">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="vertical-align:-2px;margin-right:5px"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                    Enviar para {{ props.win.conv.contact.name }}
                  </span>
                  <span v-else class="fcw-tpl-spin" style="border-top-color:#fff;border-color:rgba(255,255,255,.3)"></span>
                </button>
              </div>
            </template>
          </div>

        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, watch, onUnmounted } from 'vue'
import { useStore } from 'vuex'
import type { FloatWindow } from '@/composables/useFloatChats'
import { useFloatChats } from '@/composables/useFloatChats'
import { avatarColor, initials, conversationDocument, isFluxoGeral, confirmAssignDuringFluxoGeral, confirmResolveConversation } from '@/composables/useHelpers'
import { useSLA } from '@/composables/useSLA'
import { usePermission } from '@/composables/usePermission'
import Swal from 'sweetalert2'
import EmojiPicker from '@/components/dashboard/EmojiPicker.vue'
import { n8nDownloadUrl } from '@/api/n8n'
import { snoozeConversation } from '@/services/snoozeService'
import { listMacros, type MacroAction } from '@/services/macrosService'
import { listTeams, listTeamMembersOrAgents, type TeamItem } from '@/services/teamsService'
import { isLabelProtected } from '@/composables/useProtectedLabels'
import {
  toggleConversationStatus, assignAgentViaSystem, assignTeam, logManualAssignment,
  fetchAccountLabels, setConversationLabels,
} from '@/services/conversationActionsService'
import { runMacroAction, resolveConversationSmart, MACRO_ACTION_LABELS, type MacroActionContext } from '@/services/macroEngineService'
import { useConversationStore } from '@/composables/useConversationStore'
import { fetchMessages, fetchOlderMessages } from '@/services/messagesService'
import { useMessageCache } from '@/composables/useMessageCache'
import { onConversationMessage } from '@/composables/useChatwootMessageRealtime'
import { useMessageSearch } from '@/composables/useMessageSearch'
import { getFileType, isVideo, isPdf, fmtSize, extractFileKey } from '@/composables/useMediaHelpers'
import { useAudioRecorder } from '@/composables/useAudioRecorder'
import { useWhatsappTemplates } from '@/composables/useWhatsappTemplates'
import { useMessageSender } from '@/composables/useMessageSender'
import { useMessageWindow } from '@/composables/useMessageWindow'
import { CHATWOOT_BASE, authHeaders } from '@/api/chatwoot'
import type { ChatMessage, ProcessedMsg, LabelItem, AgentItem, Priority, SLABadge } from '@/types/chat'
import { PRIORITY_LABELS, PRIORITY_COLORS } from '@/types/chat'
import { allAgents } from '@/composables/useDashboardEngine'
import { playNotificationSound } from '@/composables/useNotificationSound'

const props = defineProps<{ win: FloatWindow }>()
defineEmits<{ close: [] }>()

const { windows, bringToFront } = useFloatChats()
// Referência mutável ao objeto na store (evita mutação direta de prop)
const mw = computed(() => windows.value.find(w => w.uid === props.win.uid)!)
const { conversations } = useConversationStore()
const store         = useStore()
const authUser      = computed(() => store.getters.authUser)
const { can, allowedTeamIds } = usePermission()
const canAssignTeam            = computed(() => can('messages.assignTeam'))

const API = `${CHATWOOT_BASE}/api/v1/accounts/3`
function apiHeaders() { return authHeaders(authUser.value?.access_token || '') }
function apiUrl(path: string) { return `${API}${path}?api_access_token=${authUser.value?.access_token || ''}` }

const {
  tplOpen: tplModalOpen, tplsLoading, tplsApproved, tplSel: tplSelected,
  tplVarNums, tplVars, tplVarMapping, tplImgUrl, tplImgErr, tplSending, tplTime,
  tplHasImg, tplHeaderTxt, tplFooterTxt, tplButtons, tplPreviewHtml,
  tplSendBlocked, tplSendBlockedReason,
  openTplModal, closeTplModal, selectTpl, setTplVar, sendTpl: sendTplToContact, mappedLabel,
} = useWhatsappTemplates({
  getContactPhone: () => props.win.conv?.contact?.phone,
  isWindowClosed:  () => isWindowClosed.value,
  getContact:      () => props.win.conv?.contact,
})

/* ── State ── */
// Singleton de módulo (useMessageCache.ts) — compartilhado com ConvDrawer.vue,
// FocoView.vue e MobileView.vue, pra mesma conversa aberta em mais de um lugar
// não buscar o histórico 2x. `msgs` continua se comportando como um ref comum
// (get/set) pro resto do arquivo — só a leitura/escrita interna aponta pro
// cache compartilhado por convId em vez de um array isolado desta instância.
const { msgCache } = useMessageCache()
const msgs = computed<ChatMessage[]>({
  get: () => msgCache.value[props.win.conv.id] ?? [],
  set: (val) => { msgCache.value = { ...msgCache.value, [props.win.conv.id]: val } },
})
const loadingMsgs = ref(false)
const loadingMore = ref(false)
const noMoreMsgs  = ref(false)
const draft       = ref('')
const attachFiles = ref<File[]>([])
const notaInterna = ref(false)
const msgsEl      = ref<HTMLElement | null>(null)
const taEl        = ref<HTMLTextAreaElement | null>(null)
const fileInputEl = ref<HTMLInputElement | null>(null)

const allLabels      = ref<LabelItem[]>([])
const labelsOpen     = ref(false)
const updatingLabels = ref(false)
const convLabels     = ref<string[]>([...props.win.conv.labels])

const {
  audioState, toggleAudio, seekAudio, audioProgress, fmtTime, disposeAudioEls,
  recording, recordingTime, recordedBlob, recordedUrl, waveformBars,
  startRecording, stopRecording, cancelRecording, sendRecordedAudio, fmtRecordingTime: fmtRecTime,
} = useAudioRecorder({
  barCount: 22,
  onSend: async (file) => { attachFiles.value = [file]; await sendMsg() },
})

let _pollTimer: ReturnType<typeof setInterval> | null = null
let _nowTimer:  ReturnType<typeof setInterval> | null = null

/* ── Status change ── */
const statusMenuOpen = ref(false)
const snoozeMode     = ref(false)
const snoozeH        = ref(1)
const snoozeM        = ref(0)
const statusLoading  = ref(false)
const nowSec         = ref(Math.floor(Date.now() / 1000))

/* ── Computed ── */
const { getProfileForFluxo: getFcwSLAProfile } = useSLA()

const fcwDocument = computed(() => {
  const conv = mw.value?.conv
  return conv ? conversationDocument(conv) : null
})

const slaBadge = computed<SLABadge | null>(() => {
  const conv = mw.value?.conv
  if (!conv || (conv.status !== 'open' && conv.status !== 'pending')) return null
  const profile = getFcwSLAProfile(conv.fluxo_id)
  if (!profile) return null
  const now = nowSec.value
  const candidates: { remaining: number; label: string; status: 'breached' | 'atRisk' }[] = []

  if (profile.resolutionTime > 0) {
    const deadline = conv.created_at + profile.resolutionTime * 60
    const remaining = deadline - now
    if (remaining <= 0) candidates.push({ remaining, label: 'Resolução', status: 'breached' })
    else if (profile.notifyBefore > 0 && remaining <= profile.notifyBefore * 60)
      candidates.push({ remaining, label: 'Resolução', status: 'atRisk' })
  }
  if (profile.firstResponseTime > 0 && conv.waiting_since > 0) {
    const deadline = conv.waiting_since + profile.firstResponseTime * 60
    const remaining = deadline - now
    if (remaining <= 0) candidates.push({ remaining, label: '1ª Resposta', status: 'breached' })
    else if (profile.notifyBefore > 0 && remaining <= profile.notifyBefore * 60)
      candidates.push({ remaining, label: '1ª Resposta', status: 'atRisk' })
  }
  if (!candidates.length) return null
  candidates.sort((a, b) => a.remaining - b.remaining)
  const worst = candidates[0]
  const abs = Math.abs(Math.floor(worst.remaining))
  const h = Math.floor(abs / 3600), m = Math.floor((abs % 3600) / 60), s = abs % 60
  const timeStr = worst.remaining <= 0
    ? `+${h > 0 ? h + 'h ' : ''}${m > 0 ? m + 'min' : s + 's'}`
    : `${h > 0 ? h + 'h ' : ''}${m > 0 ? m + 'min' : s + 's'}`
  return { label: worst.label, time: timeStr, status: worst.status }
})

const snoozeRemainingLabel = computed<string | null>(() => {
  const until = mw.value?.conv?.snoozed_until
  if (!until) return null
  const diff = until - nowSec.value
  if (diff <= 0) return null
  const h = Math.floor(diff / 3600)
  const m = Math.floor((diff % 3600) / 60)
  if (h > 0) return `${h}h ${m > 0 ? m + 'min' : ''}`
  if (m > 0) return `${m}min`
  return 'menos de 1min'
})

const processedMsgs = computed<ProcessedMsg[]>(() => {
  const list = msgs.value.filter(m => m.message_type <= 2 && (m.content || m.attachments?.length))
  return list.map((msg, i) => {
    const next = list[i + 1]
    return { ...msg, showAvatar: !next || next.message_type !== msg.message_type }
  })
})
const canSend       = computed(() => draft.value.trim().length > 0 || attachFiles.value.length > 0)
const currentLabels = computed(() => convLabels.value)
const isReadOnly    = computed(() => mw.value?.conv?.status === 'resolved')
const { isWindowClosed } = useMessageWindow(processedMsgs, nowSec)

// Notas internas exigem a janela aberta (mesmo composer da mensagem normal) — se a janela
// fechar enquanto o toggle está ativo, desativa pra não deixar o composer num estado inválido.
watch(isWindowClosed, (closed) => { if (closed) notaInterna.value = false })

/* ── Message search ── */
const {
  msgSearchOpen, msgSearchQuery, msgSearchActive, msgSearchLoading, msgCurrentIdx, msgSearchInputEl,
  msgMatchIds, getSig, highlightMsgHtml,
  openMsgSearch, closeMsgSearch, doMsgSearch, goNextMsg, goPrevMsg,
} = useMessageSearch({
  processedMsgs,
  msgsEl,
  messageSignature: () => authUser.value?.message_signature || '',
  loadMoreMsgs,
  noMoreMsgs,
})

/* ── Lifecycle ── */
// Mensagens em tempo real (Fase 5 do plano — docs/planos/PLANO-REALTIME-ACTIONCABLE.md):
// aplica a mensagem do próprio evento direto em msgs.value (onRealtimeMsg,
// sem refetch) na hora que chega message.created/updated pra esta conversa,
// em vez de esperar o próximo tick do poll de 8s abaixo — que continua
// existindo do mesmo jeito, como rede de segurança.
const msgRealtimeUnsub = onConversationMessage(props.win.conv.id, message => onRealtimeMsg(message))

onUnmounted(() => {
  if (_pollTimer) clearInterval(_pollTimer)
  if (_nowTimer)  clearInterval(_nowTimer)
  msgRealtimeUnsub()
  disposeAudioEls()
  cancelRecording()
  document.removeEventListener('click', onLblDocClick)
  document.removeEventListener('click', onStatusDocClick)
  document.removeEventListener('click', onTeamDocClick)
  pipWin?.document.removeEventListener('click', onLblDocClick)
  pipWin?.document.removeEventListener('click', onStatusDocClick)
  pipWin?.document.removeEventListener('click', onTeamDocClick)
  pipWin?.close()
})

// Se a conversa já está em msgCache (aberta antes nesta sessão, em
// ConvDrawer/FocoView/MobileView ou noutra janela flutuante), abre sem
// spinner — mesma lógica da Fase 1 de ConvDrawer.vue::openChat(). fetchMsgs
// só força scroll pro fim sozinho quando NÃO é silencioso; força aqui
// explicitamente pra cobrir os dois casos sem depender dessa heurística.
fetchMsgs(!!msgs.value.length).then(async () => { await nextTick(); scrollBottom() })
fetchAllLabels()
if (!isReadOnly.value) {
  _pollTimer = setInterval(() => fetchMsgs(true), 8000)
}
_nowTimer  = setInterval(() => { nowSec.value = Math.floor(Date.now() / 1000) }, 30_000)

/* ── Window actions ── */
function onWinClick() { bringToFront(props.win.uid) }

function toggleMinimize() { mw.value.minimized = !mw.value.minimized }

/* ── Pop-out (Document Picture-in-Picture) ──
   Move a janela pra fora do navegador, numa janela nativa sempre-visível — só existe em
   Chromium (Chrome/Edge). Enquanto pop-ada, arrastar/redimensionar ficam a cargo do próprio SO. */
const pipSupported = typeof window !== 'undefined' && 'documentPictureInPicture' in window
const isPipped = ref(false)
let pipWin: Window | null = null

function activeDocument(): Document { return (isPipped.value && pipWin) ? pipWin.document : document }

function closeAllMenus() {
  statusMenuOpen.value = false
  snoozeMode.value = false
  labelsOpen.value = false
  priorityMenuOpen.value = false
  agentMenuOpen.value = false
  teamMenuOpen.value = false
}

async function popOut() {
  if (!pipSupported || isPipped.value) return
  try {
    const dpip = (window as any).documentPictureInPicture
    // A janela flutuante na página costuma estar pequena (380x530, default de
    // useFloatChats.ts) — pop-ar nesse tamanho fica espremido. Abre já num
    // tamanho confortável, respeitando o espaço disponível na tela.
    const width  = Math.min(Math.max(Math.round(mw.value.w), 480), window.screen.availWidth - 40)
    const height = Math.min(Math.max(Math.round(mw.value.h), 680), window.screen.availHeight - 80)
    const win: Window = await dpip.requestWindow({ width, height })
    closeAllMenus() // limpa listeners de click-outside presos no document atual antes de trocar
    pipWin = win
    document.querySelectorAll('link[rel="stylesheet"], style').forEach(node => {
      win.document.head.appendChild(node.cloneNode(true))
    })
    // .fcw--pipped usa height:100% pra preencher a janela e deixar o header/
    // footer fixos com só as mensagens rolando (.fcw-msgs já é flex:1 +
    // overflow-y:auto) — mas isso só funciona com html/body com altura real,
    // senão o 100% não tem referência e a página inteira rola junto.
    win.document.documentElement.style.height = '100%'
    win.document.body.style.height = '100%'
    win.document.body.style.margin = '0'
    win.document.body.style.overflow = 'hidden'
    win.document.title = `${mw.value.conv.contact.name} · #${mw.value.conv.id}`
    isPipped.value = true
    teleportTarget.value = win.document.body
    win.addEventListener('pagehide', popIn, { once: true })
  } catch { /* usuário cancelou o pop-out ou API indisponível */ }
}

function popIn() {
  closeAllMenus()
  isPipped.value = false
  teleportTarget.value = 'body'
  pipWin = null
}

const teleportTarget = ref<string | HTMLElement>('body')

/* ── Drag ── */
let _dragging = false, _dx = 0, _dy = 0

function startDrag(e: MouseEvent) {
  if (isPipped.value) return
  _dragging = true
  _dx = e.clientX - mw.value.x
  _dy = e.clientY - mw.value.y
  document.addEventListener('mousemove', doDrag)
  document.addEventListener('mouseup', stopDrag)
  document.body.style.userSelect = 'none'
}
function doDrag(e: MouseEvent) {
  if (!_dragging) return
  mw.value.x = Math.max(0, Math.min(window.innerWidth - 80, e.clientX - _dx))
  mw.value.y = Math.max(0, Math.min(window.innerHeight - 40, e.clientY - _dy))
}
function stopDrag() {
  _dragging = false
  document.removeEventListener('mousemove', doDrag)
  document.removeEventListener('mouseup', stopDrag)
  document.body.style.userSelect = ''
}

/* ── Resize ── */
let _resizing = false, _rDir = '', _rx0 = 0, _ry0 = 0, _rw0 = 0, _rh0 = 0, _rpx0 = 0
const MIN_W = 280, MIN_H = 360, MAX_W = 700, MAX_H = 860

function startResize(dir: string) {
  if (isPipped.value) return
  _resizing = true; _rDir = dir
  _rx0 = 0; _ry0 = 0
  document.addEventListener('mousemove', doResize)
  document.addEventListener('mouseup', stopResize)
  document.body.style.userSelect = 'none'
  document.body.style.cursor = dir === 'se' ? 'se-resize' : dir === 's' ? 's-resize' : dir === 'e' ? 'e-resize' : 'w-resize'
}

function doResize(e: MouseEvent) {
  if (!_resizing) return
  if (!_rx0) { _rx0 = e.clientX; _ry0 = e.clientY; _rw0 = mw.value.w; _rh0 = mw.value.h; _rpx0 = mw.value.x; return }
  const dx = e.clientX - _rx0, dy = e.clientY - _ry0
  if (_rDir === 'se' || _rDir === 'e') mw.value.w = Math.max(MIN_W, Math.min(MAX_W, _rw0 + dx))
  if (_rDir === 'se' || _rDir === 's') mw.value.h = Math.max(MIN_H, Math.min(MAX_H, _rh0 + dy))
  if (_rDir === 'w') {
    const nw = Math.max(MIN_W, Math.min(MAX_W, _rw0 - dx))
    mw.value.x = _rpx0 + (_rw0 - nw)
    mw.value.w = nw
  }
}
function stopResize() {
  _resizing = false
  document.removeEventListener('mousemove', doResize)
  document.removeEventListener('mouseup', stopResize)
  document.body.style.userSelect = ''
  document.body.style.cursor = ''
}

/* ── Messages ── */
// Merge por id (não substitui o array inteiro): uma atualização silenciosa
// (poll de 8s/evento realtime) só traz o lote recente da API — sobrescrever
// msgs.value com isso descartava o histórico já carregado via loadMoreMsgs e
// resetava a posição de quem tava lendo mensagens antigas. Ordena de novo
// por segurança (created_at), já que o merge não garante ordem.
function mergeMsgs(existing: ChatMessage[], incoming: ChatMessage[]): ChatMessage[] {
  const byId = new Map(existing.map(m => [m.id, m]))
  incoming.forEach(m => byId.set(m.id, m))
  return [...byId.values()].sort((a, b) => a.created_at - b.created_at)
}

// "Perto do fim" = auto-scroll é esperado (chat normal); acima disso, quem
// tá lendo histórico não pode ser puxado pro fim por uma atualização em segundo plano.
function isNearBottom(): boolean {
  const el = msgsEl.value
  if (!el) return true
  return el.scrollHeight - el.scrollTop - el.clientHeight < 120
}

async function fetchMsgs(silent = true) {
  if (!silent) loadingMsgs.value = true
  noMoreMsgs.value = false
  const wasNearBottom = isNearBottom()
  try {
    const messages = await fetchMessages(props.win.conv.id)
    if (messages !== null) {
      // Toca o som só quando já havia mensagens carregadas (evita disparar pra
      // cada mensagem do histórico na primeira abertura) e a nova é do contato
      // (message_type 0 = incoming, 1 = outgoing, 2 = atividade).
      if (msgs.value.length) {
        const prevIds = new Set(msgs.value.map(m => m.id))
        if (messages.some(m => m.message_type === 0 && !prevIds.has(m.id))) playNotificationSound()
      }
      msgs.value = silent && msgs.value.length ? mergeMsgs(msgs.value, messages) : messages
    }
  } catch { /* silencioso */ } finally {
    loadingMsgs.value = false
    if (!silent || wasNearBottom) { await nextTick(); scrollBottom() }
  }
}

// Evento realtime (message.created/updated desta conversa) — usa a mensagem
// que já vem no próprio evento (ver useChatwootMessageRealtime.ts) e faz
// merge direto em msgs.value, sem golpe de rede. O poll de 8s continua
// existindo como rede de segurança (via fetchMsgs acima, já não-destrutivo).
function onRealtimeMsg(message: ChatMessage) {
  if (!msgs.value.length) { fetchMsgs(true); return } // sem cache ainda (raro) — cai pro fetch normal
  const isNewIncoming = message.message_type === 0 && !msgs.value.some(m => m.id === message.id)
  const wasNearBottom = isNearBottom()
  msgs.value = mergeMsgs(msgs.value, [message])
  if (isNewIncoming) playNotificationSound()
  if (wasNearBottom) nextTick(() => scrollBottom())
}

async function loadMoreMsgs() {
  if (loadingMore.value || noMoreMsgs.value || !msgs.value.length) return
  loadingMore.value = true
  const prevHeight = msgsEl.value?.scrollHeight ?? 0
  try {
    const result = await fetchOlderMessages(props.win.conv.id, msgs.value)
    if (result.noMore) { noMoreMsgs.value = true; return }
    if (result.messages === msgs.value) return
    msgs.value = result.messages
    await nextTick()
    if (msgsEl.value) msgsEl.value.scrollTop = msgsEl.value.scrollHeight - prevHeight
  } catch { /* silencioso */ } finally {
    loadingMore.value = false
  }
}

function onMsgsScroll() {
  if (msgsEl.value && msgsEl.value.scrollTop < 60) loadMoreMsgs()
}

const { sending, sendMsg } = useMessageSender({
  getConvId: () => props.win.conv.id,
  getToken: () => authUser.value?.access_token || '',
  getContactPhone: () => props.win.conv?.contact?.phone,
  getSig,
  draft,
  files: attachFiles,
  isPrivate: notaInterna,
  fileInputEl,
  taEl,
  scrollBottom,
  getAssigneeId: () => mw.value.conv.assignee?.id ?? null,
  getMyAgentId: () => authUser.value?.id ?? 0,
  getLabels: () => convLabels.value,
  getStatus: () => mw.value.conv.status,
  onMessageCreated: (msg) => { msgs.value = [...msgs.value, msg] },
  onLabelsChanged: (_convId, labels) => { convLabels.value = labels },
  onAutoAssigned: (_convId, agentId) => {
    const me = authUser.value
    if (!me) return
    mw.value.conv = {
      ...mw.value.conv,
      assignee: { id: agentId, name: me.name, email: me.email, role: 'agent', thumbnail: me.avatar_url, availability: 'online' },
    }
  },
  onStatusChanged: (convId, status) => {
    mw.value.conv = { ...mw.value.conv, status }
    const idx = conversations.value.findIndex(c => c.id === convId)
    if (idx !== -1) conversations.value[idx] = { ...conversations.value[idx], status }
  },
})

function scrollBottom() { if (msgsEl.value) msgsEl.value.scrollTop = msgsEl.value.scrollHeight }
function growTa() {
  const el = taEl.value; if (!el) return
  el.style.height = 'auto'
  el.style.height = Math.min(el.scrollHeight, 100) + 'px'
}

function insertEmoji(em: string) {
  const el = taEl.value
  if (!el) { draft.value += em; return }
  const start = el.selectionStart ?? draft.value.length
  const end   = el.selectionEnd   ?? draft.value.length
  draft.value = draft.value.slice(0, start) + em + draft.value.slice(end)
  nextTick(() => {
    el.selectionStart = el.selectionEnd = start + em.length
    el.focus()
    growTa()
  })
}
function onFilePick(e: Event) {
  const input = e.target as HTMLInputElement
  if (input.files) { attachFiles.value.push(...Array.from(input.files)); input.value = '' }
}

/* ── Labels ── */
async function fetchAllLabels() {
  if (allLabels.value.length) return
  try {
    allLabels.value = await fetchAccountLabels(authUser.value?.access_token || '')
  } catch { /* silencioso */ }
}

async function toggleLabel(label: string) {
  if (updatingLabels.value) return
  const current = [...convLabels.value]
  const next = current.includes(label) ? current.filter(l => l !== label) : [...current, label]
  updatingLabels.value = true
  try {
    const result = await setConversationLabels(props.win.conv.id, authUser.value?.access_token || '', next)
    if (result) convLabels.value = result
  } catch { /* silencioso */ } finally {
    updatingLabels.value = false
  }
}

function onLblDocClick(e: MouseEvent) {
  const el = activeDocument().querySelector(`.fcw-lbl-picker`)
  if (el && !el.contains(e.target as Node)) labelsOpen.value = false
}
watch(labelsOpen, open => {
  if (open) setTimeout(() => activeDocument().addEventListener('click', onLblDocClick), 0)
  else activeDocument().removeEventListener('click', onLblDocClick)
})

/* ── Status change ── */
function convStatusLabel(s: string): string {
  return ({ open: 'Aberto', resolved: 'Resolvido', pending: 'Pendente', snoozed: 'Adiado' } as Record<string, string>)[s] ?? 'Aberto'
}

function onStatusDocClick() {
  statusMenuOpen.value = false
  snoozeMode.value = false
}
watch(statusMenuOpen, open => {
  if (open) setTimeout(() => activeDocument().addEventListener('click', onStatusDocClick), 0)
  else activeDocument().removeEventListener('click', onStatusDocClick)
})

async function doChangeStatus(status: string, snoozedUntil: number | null = null) {
  if (statusLoading.value) return
  if (status === 'resolved') {
    statusMenuOpen.value = false
    if (!(await confirmResolveConversation())) return
  }
  statusLoading.value = true
  statusMenuOpen.value = false
  snoozeMode.value = false
  try {
    let newStatus: string | null
    if (status === 'snoozed') {
      const result = await snoozeConversation(props.win.conv.id, authUser.value?.access_token || '', snoozedUntil)
      newStatus = result.status
    } else if (status === 'resolved') {
      const conv = props.win.conv
      const context: MacroActionContext = {
        contactPhone:       conv?.contact?.phone ?? null,
        contactName:        conv?.contact?.name  ?? null,
        contactEmail:       null,
        teamId:             conv?.team?.id   ?? null,
        teamName:           conv?.team?.name ?? null,
        conversationStatus: conv?.status ?? null,
        contactId:          conv?.contact?.id ?? null,
        assigneeId:         conv?.assignee?.id ?? null,
        assigneeName:       conv?.assignee?.name ?? null,
        labels:             conv?.labels ?? [],
        accountId:          authUser.value?.account_id ?? null,
        executedById:       authUser.value?.id ?? null,
        executedByName:     authUser.value?.name ?? null,
      }
      const outcome = await resolveConversationSmart(props.win.conv.id, authUser.value?.access_token || '', context)
      if (!outcome.ok) return
      newStatus = outcome.status ?? 'resolved'
    } else {
      newStatus = await toggleConversationStatus(props.win.conv.id, authUser.value?.access_token || '', status)
      if (newStatus === null) return
    }
    mw.value.conv = { ...mw.value.conv, status: newStatus, snoozed_until: snoozedUntil }
    // Sincroniza com a fila/dashboard por trás da janela flutuante, sem esperar o próximo polling
    const idx = conversations.value.findIndex(c => c.id === props.win.conv.id)
    if (idx !== -1) conversations.value[idx] = { ...conversations.value[idx], status: newStatus, snoozed_until: snoozedUntil }
  } catch { /* silencioso */ } finally {
    statusLoading.value = false
  }
}

function confirmSnooze() {
  const total = (snoozeH.value || 0) * 3600 + (snoozeM.value || 0) * 60
  const until = total > 0 ? Math.floor(Date.now() / 1000) + total : null
  doChangeStatus('snoozed', until)
}

/* ── Priority ── */
const priorityMenuOpen = ref(false)
const priorityLoading  = ref(false)

const currentPriority = computed<Priority | null>(() => {
  const p = mw.value?.conv?.priority
  return (p && p !== 'none') ? (p as Priority) : null
})

async function doTogglePriority(priority: Priority) {
  if (priorityLoading.value) return
  priorityMenuOpen.value = false
  priorityLoading.value = true
  const prevPriority = mw.value.conv.priority
  mw.value.conv = { ...mw.value.conv, priority: priority === 'none' ? null : priority }
  try {
    const res = await fetch(`${API}/conversations/${props.win.conv.id}/toggle_priority`, {
      method: 'POST',
      headers: { ...apiHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ priority }),
    })
    if (!res.ok) {
      mw.value.conv = { ...mw.value.conv, priority: prevPriority }
    }
  } catch {
    mw.value.conv = { ...mw.value.conv, priority: prevPriority }
  } finally {
    priorityLoading.value = false
  }
}

function onPriorityDocClick() { priorityMenuOpen.value = false }
watch(priorityMenuOpen, open => {
  if (open) activeDocument().addEventListener('click', onPriorityDocClick)
  else activeDocument().removeEventListener('click', onPriorityDocClick)
})

/* ── Atribuição de agente ── */
const agentMenuOpen  = ref(false)
const agentLoading   = ref(false)
const teamAgents     = ref<AgentItem[]>([])
const agentSearch    = ref('')
let   _agentsFetched = false

// Status (agent_status) pra bolinha no avatar da lista — mesma fonte usada em
// TeamMembersModal.vue, não o availability_status do Chatwoot (ver useAvailability.ts).
const agentStatusByUser = ref<Record<number, string>>({})

const filteredAgents = computed(() => {
  const q = agentSearch.value.toLowerCase().trim()
  return q ? teamAgents.value.filter(a => a.name.toLowerCase().includes(q)) : teamAgents.value
})

function agentStatusOf(userId: number): string {
  return agentStatusByUser.value[userId] ?? 'offline'
}

function availLabel(status: string): string {
  return { online: 'Online', busy: 'Ocupado', offline: 'Offline' }[status] ?? 'Offline'
}

// Traz todos os agentes da conta (não só os do time da conversa) — o supervisor
// pode precisar atribuir a qualquer agente, independente do time atual.
// Status de disponibilidade vem de allAgents (useDashboardEngine) — já mantido
// por loadData() a cada ciclo (login/Atualizar/fim do countdown); list_agent_status
// não é mais chamado à parte aqui (ver useDashboardEngine.loadData()).
async function fetchAgents() {
  if (agentLoading.value || _agentsFetched) return
  _agentsFetched = true
  agentLoading.value = true
  try {
    teamAgents.value = await listTeamMembersOrAgents(authUser.value?.access_token || '', null)
    agentStatusByUser.value = Object.fromEntries(
      allAgents.value.map(a => [a.id, a.availability_status])
    )
  } catch {
    _agentsFetched = false
  } finally {
    agentLoading.value = false
  }
}

async function doAssignAgent(agentId: number | null) {
  if (agentId && isFluxoGeral(mw.value.conv.fluxo_id)) {
    if (!(await confirmAssignDuringFluxoGeral())) return
  }
  agentMenuOpen.value = false
  agentSearch.value = ''
  const prev = mw.value.conv.assignee
  const agent = agentId ? teamAgents.value.find(a => a.id === agentId) : null
  mw.value.conv = {
    ...mw.value.conv,
    assignee: agent
      ? { id: agent.id, name: agent.name, email: '', role: 'agent', thumbnail: agent.thumbnail, availability: 'online' }
      : null,
  }
  try {
    const ok = await assignAgentViaSystem(props.win.conv.id, agentId)
    if (!ok) {
      mw.value.conv = { ...mw.value.conv, assignee: prev }
    } else if (agent && authUser.value) {
      logManualAssignment(props.win.conv.id, agent.id, agent.name, props.win.conv.contact.name, authUser.value.id, authUser.value.name)
    }
  } catch {
    mw.value.conv = { ...mw.value.conv, assignee: prev }
  }
}

function onAgentDocClick() { agentMenuOpen.value = false; agentSearch.value = '' }
watch(agentMenuOpen, open => {
  if (open) { fetchAgents(); setTimeout(() => activeDocument().addEventListener('click', onAgentDocClick), 0) }
  else activeDocument().removeEventListener('click', onAgentDocClick)
})

/* ── Atribuição de time ── */
const teamMenuOpen  = ref(false)
const teamsLoading  = ref(false)
const teamsList     = ref<TeamItem[]>([])
const teamSearch    = ref('')
let   _teamsFetched = false

const filteredTeams = computed(() => {
  const allowed = allowedTeamIds()
  let list = allowed !== null ? teamsList.value.filter(t => allowed.includes(t.id)) : teamsList.value
  const q = teamSearch.value.toLowerCase().trim()
  return q ? list.filter(t => t.name.toLowerCase().includes(q)) : list
})

async function fetchTeams() {
  if (_teamsFetched) return
  teamsLoading.value = true
  try {
    teamsList.value = await listTeams(authUser.value?.access_token || '')
    _teamsFetched = true
  } catch { /* silencioso */ } finally {
    teamsLoading.value = false
  }
}

async function doAssignTeam(teamId: number | null) {
  teamMenuOpen.value = false
  teamSearch.value = ''
  const prevTeam = mw.value.conv.team
  const team = teamId ? teamsList.value.find(t => t.id === teamId) : null
  mw.value.conv = { ...mw.value.conv, team: team ? { id: team.id, name: team.name } : null }
  try {
    const ok = await assignTeam(props.win.conv.id, authUser.value?.access_token || '', teamId)
    if (!ok) mw.value.conv = { ...mw.value.conv, team: prevTeam }
  } catch {
    mw.value.conv = { ...mw.value.conv, team: prevTeam }
  }
}

function onTeamDocClick() { teamMenuOpen.value = false; teamSearch.value = '' }
watch(teamMenuOpen, open => {
  if (open) { fetchTeams(); setTimeout(() => activeDocument().addEventListener('click', onTeamDocClick), 0) }
  else activeDocument().removeEventListener('click', onTeamDocClick)
})

/* ── Helpers ── */
function senderName(msg: ChatMessage): string { return msg.sender?.name || msg.sender?.available_name || '?' }
function fmtTs(ts: number): string {
  const d = new Date(ts * 1000)
  return `${d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })} ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`
}

// ── Macro panel ───────────────────────────────────────────
interface MacroItem { id: string; name: string; description: string; actions: MacroAction[]; team_ids: number[]; is_active: boolean }

const macroPanelOpen  = ref(false)
const macrosList      = ref<MacroItem[]>([])
const macrosLoading   = ref(false)
const macroSearch     = ref('')
const macroRunning    = ref(false)
const macroRunDone    = ref(false)
const macroRunningObj = ref<MacroItem | null>(null)
const macroStepResults = ref<{ label: string; ok: boolean; msg: string }[]>([])

const filteredMacros = computed(() => {
  const q          = macroSearch.value.trim().toLowerCase()
  const convTeamId = props.win.conv.team?.id ?? null
  const active     = macrosList.value.filter(m => {
    if (!m.is_active) return false
    if (!m.team_ids?.length) return true
    return convTeamId ? m.team_ids.includes(convTeamId) : true
  })
  return q ? active.filter(m => m.name.toLowerCase().includes(q)) : active
})

async function loadMacros(force = false) {
  if (macrosList.value.length && !force) return
  macrosLoading.value = true
  try {
    macrosList.value = await listMacros(authUser.value?.access_token || '')
  } catch { /* silently fail */ }
  finally { macrosLoading.value = false }
}

function toggleMacroPanel() {
  macroPanelOpen.value = !macroPanelOpen.value
  if (macroPanelOpen.value) {
    loadMacros()
    resetMacroRun()
  }
}

function resetMacroRun() {
  macroRunning.value    = false
  macroRunDone.value    = false
  macroRunningObj.value = null
  macroStepResults.value = []
}

/**
 * Contexto pro motor de macro. Sempre parte dos dados já carregados na janela;
 * se a macro tiver alguma ação de webhook, faz um GET da conversa pra ter
 * contact_email/team atualizados (o objeto local não carrega email de contato).
 */
async function fcwMacroContext(macro: MacroItem): Promise<MacroActionContext> {
  const conv = props.win.conv
  const base: MacroActionContext = {
    contactPhone:       conv?.contact?.phone ?? null,
    contactName:        conv?.contact?.name  ?? null,
    contactEmail:       null,
    teamId:             conv?.team?.id   ?? null,
    teamName:           conv?.team?.name ?? null,
    conversationStatus: conv?.status ?? null,
    contactId:          conv?.contact?.id ?? null,
    assigneeId:         conv?.assignee?.id ?? null,
    assigneeName:       conv?.assignee?.name ?? null,
    labels:             conv?.labels ?? [],
    accountId:          authUser.value?.account_id ?? null,
    macroId:            macro.id,
    macroName:          macro.name,
    executedById:       authUser.value?.id ?? null,
    executedByName:     authUser.value?.name ?? null,
  }
  const needsLiveContext = macro.actions.some(a => a.action_name === 'send_webhook_event' || a.action_name === 'send_webhook_event_form')
  if (!needsLiveContext) return base
  try {
    const res = await fetch(apiUrl(`/conversations/${conv.id}`), { headers: apiHeaders() })
    if (!res.ok) return base
    const data = await res.json()
    return {
      ...base,
      contactPhone: data?.meta?.sender?.phone_number ?? base.contactPhone,
      contactName:  data?.meta?.sender?.name ?? base.contactName,
      contactEmail: data?.meta?.sender?.email ?? null,
      teamId:       data?.meta?.team?.id ?? base.teamId,
      teamName:     data?.meta?.team?.name ?? base.teamName,
      inboxId:      data?.inbox_id ?? null,
      inboxName:    data?.channel ?? null,
      rawConversation: data,
    }
  } catch {
    return base
  }
}

async function runMacroInChat(macro: MacroItem) {
  // Collect form values for send_webhook_event_form actions before executing
  const formActions = macro.actions
    .map((a, idx) => ({ action: a, idx }))
    .filter(({ action }) => action.action_name === 'send_webhook_event_form')

  let formValuesByIdx: Record<number, Record<string, string>> = {}

  if (formActions.length) {
    let html = '<div style="text-align:left">'
    for (const { action, idx } of formActions) {
      const cfg    = action.action_params?.[0] ?? {}
      const fields: Array<{ key: string; label: string }> = Array.isArray(cfg.fields) ? cfg.fields : []
      if (!fields.length) continue
      if (formActions.length > 1) {
        html += `<div style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.6px;color:#6b7280;margin:10px 0 6px">Webhook ${idx + 1}</div>`
      }
      for (const field of fields) {
        html += `<div style="margin-bottom:10px">
          <label style="display:block;font-size:12px;font-weight:600;color:#374151;margin-bottom:3px">${field.label || field.key}</label>
          <input id="wff_${idx}_${field.key}" data-idx="${idx}" data-key="${field.key}"
            style="width:100%;padding:7px 10px;border:1px solid #d1d5db;border-radius:6px;font-size:13px;box-sizing:border-box;font-family:inherit"
            placeholder="${field.label || field.key}" />
        </div>`
      }
    }
    html += '</div>'

    const result = await Swal.fire({
      title: 'Preencha os campos',
      html,
      showCancelButton: true,
      confirmButtonText: 'Executar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#2563eb',
      cancelButtonColor: '#6b7280',
      focusConfirm: false,
      preConfirm: () => {
        const inputs = Swal.getHtmlContainer()?.querySelectorAll('input[data-idx]') ?? []
        const values: Record<number, Record<string, string>> = {}
        inputs.forEach((el: any) => {
          const i   = Number(el.dataset.idx)
          const key = String(el.dataset.key)
          if (!values[i]) values[i] = {}
          values[i][key] = el.value
        })
        return values
      },
    })

    if (!result.isConfirmed) return
    formValuesByIdx = (result.value as Record<number, Record<string, string>>) ?? {}
  }

  macroRunningObj.value  = macro
  macroStepResults.value = []
  macroRunDone.value     = false
  macroRunning.value     = true
  const convId = props.win.conv.id
  const context = await fcwMacroContext(macro)
  for (const [idx, action] of macro.actions.entries()) {
    const formVals = action.action_name === 'send_webhook_event_form'
      ? (formValuesByIdx[idx] ?? {})
      : undefined
    macroStepResults.value.push(
      await runMacroAction(action, convId, authUser.value?.access_token || '', context, formVals)
    )
  }
  macroRunning.value = false
  macroRunDone.value = true
}
</script>

<style scoped>
/* ── Base da janela ── */
.fcw {
  position: fixed;
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background: var(--white);
  box-shadow: 0 8px 40px rgba(0,0,0,.22), 0 2px 10px rgba(0,0,0,.14);
  border: 1px solid var(--ink-10);
  min-width: 280px;
  min-height: 40px;
  transition: box-shadow .2s ease;
}
.fcw:focus-within,
.fcw:hover { box-shadow: 0 12px 52px rgba(0,0,0,.28), 0 3px 12px rgba(0,0,0,.16); }

/* Pop-ada numa Document Picture-in-Picture window: ela já é a própria janela do SO,
   então preenche 100% em vez de flutuar sobre a página. */
.fcw--pipped,
.fcw--pipped:hover,
.fcw--pipped:focus-within {
  position: static;
  border-radius: 0;
  border: none;
  box-shadow: none;
  min-width: 0;
}

/* ── Titlebar ── */
.fcw-head {
  display: flex; align-items: center; gap: 8px;
  padding: 8px 8px 8px 10px; min-height: 52px;
  background: #1a0407;
  cursor: grab; flex-shrink: 0; user-select: none;
  border-bottom: 1px solid rgba(0,0,0,.25);
}
.fcw-head:active { cursor: grabbing; }
.fcw-av {
  width: 28px; height: 28px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 10px; font-weight: 700; color: #fff; overflow: hidden;
  border: 1.5px solid rgba(255,255,255,.3);
}
.fcw-av img { width: 100%; height: 100%; object-fit: cover; }
.fcw-head-info { flex: 1; min-width: 0; }
.fcw-head-name { font-size: 12.5px; font-weight: 700; color: #fff; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.fcw-head-doc  { font-size: 9.5px; color: rgba(255,255,255,.5); margin-top: 1px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.fcw-head-sub  { font-size: 10px; color: rgba(255,255,255,.6); margin-top: 2px; display: flex; align-items: center; gap: 4px; }

/* Status label pill in float chat header */
.fcw-status-lbl {
  display: inline-flex; align-items: center; flex-shrink: 0;
  padding: 2px 7px; border-radius: 4px;
  font-size: 9.5px; font-weight: 800; letter-spacing: .5px; text-transform: uppercase;
}
.fcw-status-lbl.open     { background: rgba(34,197,94,.22); color: #86efac; border: 1px solid rgba(34,197,94,.3); }
.fcw-status-lbl.pending  { background: rgba(249,115,22,.25); color: #fdba74; border: 1px solid rgba(249,115,22,.35); }
.fcw-status-lbl.snoozed  { background: rgba(245,158,11,.25); color: #fcd34d; border: 1px solid rgba(245,158,11,.35); }
.fcw-status-lbl.resolved { background: rgba(255,255,255,.1);  color: rgba(255,255,255,.6); border: 1px solid rgba(255,255,255,.18); }

/* Agent tag in header */
.fcw-agent-tag {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 2px 7px; border-radius: 6px; flex-shrink: 0;
  font-size: 10px; font-weight: 600;
  background: rgba(255,255,255,.1); color: rgba(255,255,255,.8);
  border: 1px solid rgba(255,255,255,.18);
}
.fcw-agent-tag svg { opacity: .75; flex-shrink: 0; }

/* SLA badge in header */
.fcw-sla-badge {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 2px 7px; border-radius: 6px; flex-shrink: 0;
  font-size: 10px;
}
.fcw-sla-badge strong { font-size: 11px; font-weight: 800; }
.fcw-sla-badge.breached { background: rgba(239,68,68,.25); color: #fca5a5; border: 1px solid rgba(239,68,68,.35); }
.fcw-sla-badge.atRisk   { background: rgba(249,115,22,.25); color: #fdba74; border: 1px solid rgba(249,115,22,.35); }

.fcw-head-btns { display: flex; align-items: center; gap: 2px; flex-shrink: 0; }
.fcw-btn {
  width: 26px; height: 26px; border-radius: 6px; border: none;
  background: rgba(255,255,255,.1); color: rgba(255,255,255,.75);
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; text-decoration: none;
  transition: background .15s, color .15s;
}
.fcw-btn:hover { background: rgba(255,255,255,.22); color: #fff; }
.fcw-btn-close:hover { background: rgba(220,50,50,.45); color: #fff; }

/* Minimizado */
.fcw--minimized { border-radius: 10px; }
.fcw--minimized .fcw-head { border-radius: 10px; }

/* ── Etiquetas ── */
.fcw-lbls {
  display: flex; align-items: center; gap: 5px; flex-wrap: wrap;
  padding: 6px 10px; border-bottom: 1px solid var(--ink-05);
  background: var(--ink-02); flex-shrink: 0;
}
.fcw-lbl {
  display: inline-flex; align-items: center; gap: 3px;
  background: rgba(188,12,36,.09); color: var(--red);
  border: 1px solid rgba(188,12,36,.2); border-radius: 999px;
  padding: 2px 5px 2px 8px; font-size: 10.5px; font-weight: 600;
}
.fcw-lbl-x {
  background: none; border: none; cursor: pointer; color: var(--red);
  font-size: 13px; line-height: 1; padding: 0; opacity: .7;
}
.fcw-lbl-x:hover { opacity: 1; }
.fcw-lbl-lock { flex-shrink: 0; opacity: .6; }
.fcw-lbl-picker { position: relative; flex-shrink: 0; }
.fcw-lbl-add {
  display: inline-flex; align-items: center; gap: 4px;
  background: none; border: 1px dashed var(--ink-10); border-radius: 999px;
  padding: 2px 8px; font-size: 10.5px; font-weight: 500;
  color: var(--ink-40); cursor: pointer; font-family: inherit;
  transition: border-color .15s, color .15s;
}
.fcw-lbl-add:hover { border-color: var(--red); color: var(--red); }
.fcw-lbl-dropdown {
  position: absolute; top: calc(100% + 5px); left: 0; z-index: 10;
  min-width: 180px; background: var(--white); border: 1px solid var(--ink-10);
  border-radius: 10px; box-shadow: 0 6px 24px rgba(0,0,0,.15);
  overflow: hidden; max-height: 220px; overflow-y: auto;
}
.fcw-lbl-loading { padding: 12px; display: flex; justify-content: center; }
.fcw-lbl-opt {
  display: flex; align-items: center; gap: 7px; padding: 8px 10px;
  font-size: 12px; cursor: pointer; transition: background .12s;
}
.fcw-lbl-opt:hover { background: var(--ink-02); }
.fcw-lbl-opt.checked { background: rgba(188,12,36,.04); }
.fcw-lbl-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }

/* ── Prioridade ── */
.fcw-priority-wrap { position: relative; flex-shrink: 0; }
.fcw-priority-tag {
  display: inline-flex; align-items: center; gap: 4px;
  border: 1px solid; border-radius: 999px;
  padding: 2px 8px; font-size: 10.5px; font-weight: 700;
  cursor: pointer; transition: opacity .15s;
  user-select: none;
}
.fcw-priority-tag:hover { opacity: .8; }
.fcw-priority-add { }
.fcw-priority-menu {
  position: absolute; top: calc(100% + 5px); left: 0; z-index: 20;
  min-width: 140px; background: var(--white); border: 1px solid var(--ink-10);
  border-radius: 10px; box-shadow: 0 6px 24px rgba(0,0,0,.15);
  overflow: hidden;
}
.fcw-priority-opt {
  display: flex; align-items: center; gap: 7px;
  padding: 7px 10px; width: 100%; border: none; background: none;
  font-size: 12px; cursor: pointer; transition: background .12s;
  font-family: inherit; color: var(--ink-70); text-align: left;
}
.fcw-priority-opt:hover { background: var(--ink-02); }
.fcw-priority-opt.active { background: rgba(34,197,94,.05); }
.fcw-priority-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
.fcw-p-spin {
  display: inline-block; width: 8px; height: 8px;
  border: 1.5px solid rgba(255,255,255,.4); border-top-color: currentColor;
  border-radius: 50%; animation: fcw-spin .7s linear infinite;
}

/* ── Agente selector ── */
.fcw-agent-wrap { position: relative; flex-shrink: 0; }
.fcw-agent-sel {
  display: inline-flex; align-items: center; gap: 4px;
  border: 1px solid var(--ink-10); border-radius: 999px;
  padding: 2px 8px; font-size: 10.5px; font-weight: 600;
  color: var(--ink-60); cursor: pointer; background: var(--ink-02);
  transition: border-color .15s, color .15s;
}
.fcw-agent-sel:hover { border-color: var(--red); color: var(--red); }
.fcw-agent-menu {
  position: absolute; top: calc(100% + 5px); left: 0; z-index: 25;
  width: 210px; background: var(--white); border: 1px solid var(--ink-10);
  border-radius: 10px; box-shadow: 0 6px 24px rgba(0,0,0,.2); overflow: hidden;
}
.fcw-agent-search-wrap { padding: 7px 8px; border-bottom: 1px solid var(--ink-05); }
.fcw-agent-search {
  width: 100%; border: 1px solid var(--ink-10); border-radius: 7px;
  padding: 5px 8px; font-size: 11.5px; outline: none; box-sizing: border-box;
  background: var(--white); color: var(--ink-80); font-family: inherit;
}
.fcw-agent-search:focus { border-color: var(--red); }
.fcw-agent-list { max-height: 200px; overflow-y: auto; }
.fcw-agent-opt {
  display: flex; align-items: center; gap: 7px; width: 100%;
  border: none; background: none; padding: 7px 10px;
  font-size: 12px; cursor: pointer; transition: background .12s;
  font-family: inherit; color: var(--ink-70); text-align: left;
  white-space: nowrap; overflow: hidden;
}
.fcw-agent-opt:hover { background: var(--ink-02); }
.fcw-agent-opt.active { background: rgba(34,197,94,.05); }
.fcw-agent-av {
  width: 20px; height: 20px; border-radius: 50%; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  font-size: 8px; font-weight: 700; color: #fff;
}
.fcw-agent-av-wrap { position: relative; flex-shrink: 0; display: inline-flex; }
.fcw-agent-status-dot {
  position: absolute; bottom: -1px; right: -1px;
  width: 7px; height: 7px; border-radius: 50%;
  border: 1.5px solid var(--white);
  box-shadow: 0 1px 3px rgba(0,0,0,.18);
}
.fasd-online  { background: var(--green); }
.fasd-busy    { background: var(--orange); }
.fasd-offline { background: var(--ink-40); }

/* ── Mensagens ── */
.fcw-msgs {
  flex: 1; overflow-y: auto; padding: 12px 10px;
  display: flex; flex-direction: column; gap: 4px;
  min-height: 0;
}
.fcw-msgs-loading { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px; padding: 24px; flex: 1; }
.fcw-msgs-loading-label { font-size: 12px; color: #888; }
.fcw-load-more-row { display: flex; justify-content: center; padding: 10px 0; }
.fcw-spinner-sm { display: inline-block; width: 16px; height: 16px; border: 2px solid rgba(0,0,0,.12); border-top-color: #888; border-radius: 50%; animation: fcw-spin .7s linear infinite; }
.fcw-no-more { text-align: center; font-size: 11px; color: #bbb; padding: 8px 0; }
.fcw-msg-row { display: flex; align-items: flex-end; gap: 6px; }
.fcw-msg-row.out { flex-direction: row-reverse; }
.fcw-msg-act { width: 100%; text-align: center; font-size: 10.5px; color: var(--ink-40); padding: 4px 0; font-style: italic; }

.fcw-msg-av {
  width: 24px; height: 24px; border-radius: 50%; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  font-size: 9px; font-weight: 700; color: #fff; overflow: hidden;
}
.fcw-msg-av img { width: 100%; height: 100%; object-fit: cover; }
.fcw-msg-av-spacer { width: 24px; flex-shrink: 0; }

.fcw-bubble {
  max-width: 74%; display: flex; flex-direction: column; gap: 4px;
}
.out .fcw-bubble { align-items: flex-end; }
.fcw-bubble-text {
  background: var(--ink-05); color: var(--ink-80);
  border-radius: 12px 12px 12px 2px; padding: 7px 10px;
  font-size: 12.5px; line-height: 1.45; word-break: break-word;
  display: inline-block;
}
.out .fcw-bubble-text {
  background: var(--red); color: #fff;
  border-radius: 12px 12px 2px 12px;
}
.out .fcw-bubble-text .msg-sig {
  display: block;
  margin-top: 7px;
  padding-top: 6px;
  border-top: 1px solid rgba(255,255,255,0.28);
  font-size: 10.5px;
  font-style: italic;
  opacity: 0.7;
  letter-spacing: 0.01em;
}
.fcw-bubble-ts { font-size: 9.5px; color: var(--ink-40); padding: 0 4px; display: inline-flex; align-items: center; gap: 2px; }
.fcw-priv-tag { display: flex; align-items: center; gap: 4px; font-size: 9.5px; font-weight: 700; text-transform: uppercase; letter-spacing: .03em; color: #92400E; padding: 0 4px; }
.out .fcw-priv-tag { align-self: flex-end; }
.fcw-bubble-text--priv { background: #FEF3C7 !important; color: #78350F !important; border-radius: 12px !important; }
.fcw-msg-status { display: inline-flex; align-items: center; flex-shrink: 0; }
.fcw-file-dl-btn {
  display: inline-flex; align-items: center; gap: 5px;
  margin-top: 4px; padding: 4px 9px; border-radius: 6px;
  background: rgba(255,255,255,.18); color: #fff;
  font-size: 11px; font-weight: 600; text-decoration: none;
  transition: background .15s; align-self: flex-start;
}
.fcw-file-dl-btn:hover { background: rgba(255,255,255,.30); }
.fcw-msg-status.s-read { color: #3B82F6; }
.fcw-msg-status.s-failed { color: #EF4444; }

/* ── Anexos ── */
.fcw-att { display: flex; flex-direction: column; gap: 4px; }
.fcw-att-img-wrap { display: block; border-radius: 8px; overflow: hidden; max-width: 200px; }
.fcw-att-img { display: block; width: 100%; max-height: 160px; object-fit: cover; border-radius: 8px; }
.fcw-att-video-wrap { border-radius: 8px; overflow: hidden; max-width: 220px; }
.fcw-att-video { display: block; width: 100%; border-radius: 8px; }
.fcw-att-file {
  display: inline-flex; align-items: center; gap: 8px;
  background: var(--ink-05); border: 1px solid var(--ink-10);
  border-radius: 8px; padding: 8px 10px; text-decoration: none;
  color: var(--ink-60); font-size: 12px; max-width: 200px;
}
.fcw-att-pdf { border-color: rgba(188,12,36,.2); color: var(--red); }
.fcw-att-info { display: flex; flex-direction: column; }
.fcw-att-ext { font-size: 11px; font-weight: 700; }
.fcw-att-size { font-size: 10px; color: var(--ink-40); }

/* ── Audio player ── */
.fcw-ap { display: flex; align-items: center; gap: 7px; background: var(--ink-05); border-radius: 20px; padding: 6px 10px; max-width: 220px; }
.fcw-ap-btn { width: 28px; height: 28px; border-radius: 50%; border: none; background: var(--red); color: #fff; display: flex; align-items: center; justify-content: center; cursor: pointer; flex-shrink: 0; }
.fcw-ap-mid { flex: 1; display: flex; flex-direction: column; gap: 3px; min-width: 0; }
.fcw-ap-bar { position: relative; height: 4px; background: var(--ink-10); border-radius: 99px; cursor: pointer; }
.fcw-ap-fill { height: 100%; background: var(--red); border-radius: 99px; pointer-events: none; }
.fcw-ap-seek { position: absolute; inset: -4px 0; opacity: 0; width: 100%; cursor: pointer; }
.fcw-ap-time { display: flex; justify-content: space-between; font-size: 9px; color: var(--ink-40); }
.fcw-ap-dl { color: var(--ink-40); display: flex; }
.fcw-ap-dl:hover { color: var(--red); }

/* ── File preview ── */
.fcw-files {
  display: flex; flex-wrap: wrap; gap: 5px; padding: 6px 10px;
  border-top: 1px solid var(--ink-05); background: var(--ink-02); flex-shrink: 0;
}
.fcw-file-pill {
  display: inline-flex; align-items: center; gap: 4px;
  background: rgba(188,12,36,.07); border: 1px solid rgba(188,12,36,.18);
  border-radius: 999px; padding: 3px 6px 3px 8px;
  font-size: 10.5px; color: var(--red); max-width: 160px;
}
.fcw-file-pill span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.fcw-file-pill button { background: none; border: none; cursor: pointer; color: var(--red); font-size: 13px; padding: 0; flex-shrink: 0; }

/* ── Compositor ── */
.fcw-composer {
  display: flex; align-items: center; gap: 6px;
  padding: 8px 10px; border-top: 1px solid var(--ink-05);
  background: var(--white); flex-shrink: 0;
  transition: background .15s, border-color .15s;
}
.fcw-composer--priv { background: #FFFBEB; border-top-color: #FDE68A; }
.fcw-attach {
  width: 30px; height: 30px; border-radius: 8px; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  color: var(--ink-40); cursor: pointer; transition: color .15s, background .15s;
}
.fcw-attach:hover { background: var(--ink-05); color: var(--ink-60); }
.fcw-attach.fcw-attach-disabled { opacity: .4; cursor: not-allowed; }
.fcw-attach.fcw-attach-disabled:hover { background: none; color: var(--ink-40); }
.fcw-priv-btn {
  width: 30px; height: 30px; border-radius: 8px; flex-shrink: 0; border: none;
  display: flex; align-items: center; justify-content: center;
  color: var(--ink-40); background: none; cursor: pointer; transition: color .15s, background .15s;
}
.fcw-priv-btn:hover { background: var(--ink-05); color: #92400E; }
.fcw-priv-btn:disabled { opacity: .4; cursor: not-allowed; }
.fcw-priv-btn--active, .fcw-priv-btn--active:hover { background: #F59E0B; color: #fff; }
.fcw-ta {
  flex: 1; border: 1.5px solid var(--ink-10); border-radius: 8px;
  padding: 6px 9px; font-size: 12.5px; font-family: inherit;
  background: var(--ink-02); color: var(--ink-80); resize: none;
  outline: none; line-height: 1.45; max-height: 100px;
  transition: border-color .15s;
}
.fcw-composer--priv .fcw-ta { background: #FFFBEB; border-color: #FDE68A; }
.fcw-ta:focus { border-color: var(--red); background: var(--white); }
.fcw-ta:disabled { background: var(--ink-05); color: var(--ink-40); cursor: not-allowed; }
.fcw-mic, .fcw-send {
  width: 32px; height: 32px; border-radius: 8px; flex-shrink: 0;
  border: none; display: flex; align-items: center; justify-content: center; cursor: pointer;
  transition: background .15s, transform .15s;
}
.fcw-mic { background: var(--ink-05); color: var(--ink-40); }
.fcw-mic:hover { background: var(--ink-10); color: var(--ink-60); }
.fcw-send { background: var(--red); color: #fff; }
.fcw-send:hover { background: var(--red-dark); transform: scale(1.06); }
.fcw-send:disabled { opacity: .45; cursor: default; transform: none; }

/* ── Gravação ── */
.fcw-recording, .fcw-rec-preview {
  display: flex; align-items: center; gap: 8px;
  padding: 8px 10px; border-top: 1px solid var(--ink-05);
  background: var(--white); flex-shrink: 0;
}
.fcw-rec-cancel {
  width: 28px; height: 28px; border-radius: 50%; border: none; flex-shrink: 0;
  background: var(--ink-05); color: var(--ink-40); cursor: pointer;
  display: flex; align-items: center; justify-content: center;
}
.fcw-rec-cancel:hover { background: rgba(188,12,36,.1); color: var(--red); }
.fcw-rec-dot { width: 8px; height: 8px; border-radius: 50%; background: var(--red); flex-shrink: 0; animation: fcw-blink .9s ease-in-out infinite; }
@keyframes fcw-blink { 0%,100%{opacity:1} 50%{opacity:.2} }
.fcw-rec-wave { flex: 1; display: flex; align-items: center; gap: 2px; height: 22px; }
.fcw-rec-bar { width: 3px; border-radius: 999px; background: var(--red); transition: height .06s ease; }
.fcw-rec-time { font-size: 12px; font-weight: 700; color: var(--red); flex-shrink: 0; font-variant-numeric: tabular-nums; }
.fcw-rec-stop {
  width: 28px; height: 28px; border-radius: 50%; border: none; flex-shrink: 0;
  background: var(--red); color: #fff; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
}
.fcw-rec-audio { flex: 1; height: 32px; min-width: 0; }
.fcw-rec-send {
  width: 30px; height: 30px; border-radius: 8px; border: none; flex-shrink: 0;
  background: var(--red); color: #fff; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
}

/* ── Handles de redimensionamento ── */
.fcw-resize-se {
  position: absolute; bottom: 0; right: 0; width: 14px; height: 14px; cursor: se-resize; z-index: 2;
}
.fcw-resize-se::after {
  content: ''; position: absolute; bottom: 3px; right: 3px;
  width: 6px; height: 6px; border-right: 2px solid var(--ink-10); border-bottom: 2px solid var(--ink-10);
}
.fcw-resize-s { position: absolute; bottom: 0; left: 14px; right: 14px; height: 6px; cursor: s-resize; z-index: 1; }
.fcw-resize-e { position: absolute; top: 44px; right: 0; width: 6px; bottom: 14px; cursor: e-resize; z-index: 1; }
.fcw-resize-w { position: absolute; top: 44px; left: 0; width: 6px; bottom: 14px; cursor: w-resize; z-index: 1; }

/* ── Spinners ── */
.fcw-spinner {
  display: inline-block; width: 22px; height: 22px;
  border: 2.5px solid var(--ink-10); border-top-color: var(--red);
  border-radius: 50%; animation: fcw-spin .7s linear infinite;
}
/* Botão de enviar tem fundo colorido — spinner claro. Nome diferente do
   .fcw-spinner-sm de cima (load more, sobre fundo claro) de propósito: as duas
   regras já colidiram uma vez (mesma especificidade, a última do arquivo vencia
   e deixava o spinner do "carregar mais antigas" branco-sobre-claro, invisível). */
.fcw-spinner-sm-white {
  display: inline-block; width: 12px; height: 12px;
  border: 2px solid rgba(255,255,255,.3); border-top-color: #fff;
  border-radius: 50%; animation: fcw-spin .7s linear infinite;
}
@keyframes fcw-spin { to { transform: rotate(360deg); } }

/* ── Botão WhatsApp template ── */
.fcw-wa-tpl {
  width: 32px; height: 32px; border-radius: 8px; flex-shrink: 0;
  border: none; display: flex; align-items: center; justify-content: center;
  cursor: pointer; transition: background .15s, color .15s;
  background: var(--ink-05); color: var(--ink-40);
}
.fcw-wa-tpl:hover { background: var(--ink-10); color: var(--ink-60); }

/* ── Modal de templates ── */
.fcw-tpl-overlay {
  position: fixed; inset: 0; z-index: 9000;
  background: rgba(0,0,0,.55); backdrop-filter: blur(2px);
  display: flex; align-items: center; justify-content: center;
  padding: 16px;
}
.fcw-tpl-dialog {
  background: #fff; border-radius: 12px;
  box-shadow: 0 24px 64px rgba(0,0,0,.22);
  width: min(780px, 100%); max-height: calc(100vh - 32px);
  display: flex; flex-direction: column; overflow: hidden;
}
.fcw-tpl-head {
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 18px; border-bottom: 1px solid #e5e7eb; flex-shrink: 0;
}
.fcw-tpl-title {
  display: flex; align-items: center; gap: 8px;
  font-size: 14px; font-weight: 700; color: #111;
}
.fcw-tpl-contact {
  font-size: 12px; font-weight: 500; color: #6b7280; margin-left: 4px;
}
.fcw-tpl-close {
  background: none; border: none; cursor: pointer; padding: 4px;
  color: #9ca3af; border-radius: 6px; display: flex; transition: background .15s;
}
.fcw-tpl-close:hover { background: #f3f4f6; color: #374151; }
.fcw-tpl-body {
  display: grid; grid-template-columns: 260px 1fr;
  overflow: hidden; flex: 1;
}
.fcw-tpl-list-col {
  padding: 14px; border-right: 1px solid #e5e7eb;
  overflow-y: auto; display: flex; flex-direction: column; gap: 6px;
}
.fcw-tpl-config-col {
  padding: 14px 18px; overflow-y: auto;
}
.fcw-tpl-col-label {
  display: flex; align-items: center; gap: 5px;
  font-size: 10px; font-weight: 700; color: #6b7280;
  text-transform: uppercase; letter-spacing: .06em; margin-bottom: 8px;
}
.fcw-tpl-loading, .fcw-tpl-empty {
  display: flex; align-items: center; gap: 6px;
  color: #9ca3af; font-size: 12px; padding: 12px 0;
}
.fcw-tpl-items { display: flex; flex-direction: column; gap: 4px; }
.fcw-tpl-item {
  width: 100%; text-align: left; padding: 8px 10px;
  border: 1.5px solid #e5e7eb; border-radius: 7px;
  background: #fff; cursor: pointer; transition: border-color .15s, background .15s;
}
.fcw-tpl-item:hover { background: #f9fafb; border-color: #d1d5db; }
.fcw-tpl-item.active { border-color: #25D366; background: rgba(37,211,102,.05); }
.fcw-tpl-item-name {
  font-size: 12px; font-weight: 600; color: #111;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis; margin-bottom: 4px;
}
.fcw-tpl-item-meta { display: flex; align-items: center; gap: 5px; }
.fcw-tpl-cat {
  font-size: 9.5px; font-weight: 700; text-transform: uppercase;
  padding: 1px 5px; border-radius: 4px;
}
.fcw-tpl-cat.marketing { background: rgba(251,191,36,.15); color: #b45309; }
.fcw-tpl-cat.utility   { background: rgba(59,130,246,.12); color: #1d4ed8; }
.fcw-tpl-lang { font-size: 10px; color: #9ca3af; }
.fcw-tpl-pick-hint {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 10px; height: 100%; color: #9ca3af; font-size: 12px; text-align: center;
  padding: 40px 20px;
}
.fcw-tpl-preview-wrap {
  background: #e5ddd5; border-radius: 8px; padding: 10px;
}
.fcw-tpl-bubble {
  background: #fff; border-radius: 0 10px 10px 10px;
  padding: 10px; max-width: 90%; box-shadow: 0 1px 3px rgba(0,0,0,.1);
  font-family: -apple-system,sans-serif;
}
.fcw-tpl-bubble-img {
  width: 100%; border-radius: 7px; height: 110px; overflow: hidden;
  margin-bottom: 8px; background: #c8d8e0;
  display: flex; align-items: center; justify-content: center;
}
.fcw-tpl-bubble-img img { width:100%; height:100%; object-fit:cover; }
.fcw-tpl-img-ph { display:flex; align-items:center; justify-content:center; color:#8a9ca5; }
.fcw-tpl-bubble-hdr { font-size: 13px; font-weight: 700; color: #1a1a1a; margin-bottom: 4px; }
.fcw-tpl-bubble-body { font-size: 12.5px; color: #1a1a1a; line-height: 1.5; white-space: pre-wrap; word-break: break-word; }
.fcw-tpl-bubble-ftr { font-size: 10.5px; color: #8a8a8a; margin-top: 6px; padding-top: 6px; border-top: 1px solid #f0f0f0; }
.fcw-tpl-bubble-time { text-align: right; font-size: 9.5px; color: #999; margin-top: 3px; }
.fcw-tpl-btns { display: flex; flex-direction: column; gap: 4px; margin-top: 4px; }
.fcw-tpl-btn {
  background: #fff; border-radius: 8px; padding: 7px 10px;
  font-size: 12px; font-weight: 500; color: #00a5f4; text-align: center;
  box-shadow: 0 1px 3px rgba(0,0,0,.08);
}
.fcw-tpl-field-label {
  font-size: 10px; font-weight: 700; color: #6b7280;
  text-transform: uppercase; letter-spacing: .05em; margin-bottom: 3px;
}
.fcw-tpl-input {
  width: 100%; padding: 7px 10px; border: 1.5px solid #e2e8f0;
  border-radius: 6px; font-size: 12.5px; font-family: inherit;
  outline: none; box-sizing: border-box; transition: border-color .15s;
}
.fcw-tpl-input:focus { border-color: #25D366; }
.fcw-tpl-hint { font-size: 10px; color: #9ca3af; margin-top: 2px; }
.fcw-tpl-mapped-badge {
  display: flex; align-items: center; gap: 6px;
  background: rgba(255,102,0,.08); color: #cc5200;
  border: 1px solid rgba(255,102,0,.2); border-radius: 6px;
  padding: 7px 10px; font-size: 12px; font-weight: 600;
}
.fcw-tpl-send-btn {
  width: 100%; padding: 10px; background: #25D366; color: #fff;
  border: none; border-radius: 8px; font-size: 13px; font-weight: 600;
  font-family: inherit; cursor: pointer; display: flex; align-items: center;
  justify-content: center; transition: background .15s;
}
.fcw-tpl-send-btn:hover:not(:disabled) { background: #1db954; }
.fcw-tpl-send-btn:disabled { opacity: .5; cursor: not-allowed; }
.fcw-tpl-blocked-hint { font-size: 11.5px; color: #b45309; background: #fffbeb; border: 1px solid #fde68a; border-radius: 6px; padding: 6px 8px; margin-bottom: 8px; line-height: 1.4; }
.fcw-tpl-spin {
  display: inline-block; width: 14px; height: 14px;
  border: 2px solid rgba(0,0,0,.15); border-top-color: #374151;
  border-radius: 50%; animation: fcw-spin .7s linear infinite;
}

/* ── Snooze clock badge on avatar ── */
.fcw-av-wrap { position: relative; flex-shrink: 0; }
.fcw-av-snooze {
  position: absolute; bottom: -2px; right: -2px;
  width: 14px; height: 14px; border-radius: 50%;
  background: #f59e0b; border: 1.5px solid rgba(188,12,36,1);
  display: flex; align-items: center; justify-content: center;
  color: #fff;
}

/* ── Snooze banner ── */
.fcw-snooze-banner {
  display: flex; align-items: center; gap: 6px;
  padding: 6px 12px; background: rgba(245,158,11,.1);
  border-bottom: 1px solid rgba(245,158,11,.25);
  font-size: 11.5px; font-weight: 600; color: #92400e;
  flex-shrink: 0;
}
.fcw-snooze-banner svg { flex-shrink: 0; color: #f59e0b; }

/* ── Status change ── */
.fcw-status-wrap { position: relative; display: flex; align-items: center; }

.fcw-s-dot {
  width: 8px; height: 8px; border-radius: 50%; display: inline-block; flex-shrink: 0;
}
.fcw-s-dot.open     { background: #22c55e; }
.fcw-s-dot.resolved { background: #6b7280; }
.fcw-s-dot.pending  { background: #f97316; }
.fcw-s-dot.snoozed  { background: #f59e0b; }

.fcw-status-menu {
  position: absolute; top: calc(100% + 6px); right: 0; z-index: 50;
  min-width: 148px; background: var(--white);
  border: 1px solid var(--ink-10); border-radius: 10px;
  box-shadow: 0 8px 28px rgba(0,0,0,.18);
  overflow: hidden;
}
.fcw-sopt {
  display: flex; align-items: center; gap: 8px; width: 100%;
  padding: 9px 12px; background: none; border: none;
  font-size: 12.5px; font-weight: 500; color: var(--ink-80);
  cursor: pointer; font-family: inherit; text-align: left;
  transition: background .12s;
}
.fcw-sopt:hover { background: var(--ink-02); }

/* Snooze form inside status menu */
.fcw-snz { padding: 12px; display: flex; flex-direction: column; gap: 8px; min-width: 200px; }
.fcw-snz-title { font-size: 11.5px; font-weight: 700; color: var(--ink-80); }
.fcw-snz-row { display: flex; align-items: center; gap: 6px; }
.fcw-snz-inp {
  width: 52px; padding: 5px 7px; border: 1.5px solid var(--ink-10);
  border-radius: 6px; font-size: 13px; font-family: inherit;
  background: var(--ink-02); outline: none; text-align: center;
}
.fcw-snz-inp:focus { border-color: var(--red); background: var(--white); }
.fcw-snz-lbl { font-size: 12px; color: var(--ink-60); font-weight: 500; }
.fcw-snz-hint { font-size: 10px; color: var(--ink-40); }
.fcw-snz-btns { display: flex; gap: 6px; }
.fcw-snz-cancel {
  flex: 1; padding: 7px; border: 1px solid var(--ink-10); border-radius: 6px;
  background: var(--white); font-size: 12px; font-family: inherit;
  color: var(--ink-60); cursor: pointer;
}
.fcw-snz-cancel:hover { background: var(--ink-02); }
.fcw-snz-confirm {
  flex: 1; padding: 7px; border: none; border-radius: 6px;
  background: var(--red); color: #fff; font-size: 12px; font-family: inherit;
  font-weight: 600; cursor: pointer;
}
.fcw-snz-confirm:hover:not(:disabled) { background: var(--red-dark); }
.fcw-snz-confirm:disabled { opacity: .5; cursor: not-allowed; }

/* ── Read-only banner ─────────────────────────────────── */
.fcw-readonly-banner {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 10px 14px;
  background: #f0fdf4;
  border-top: 1px solid #bbf7d0;
  font-size: 12px;
  font-weight: 600;
  color: #15803d;
  flex-shrink: 0;
}
.fcw-window-banner {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 8px 14px;
  background: #fef2f2;
  border-top: 1px solid #fecaca;
  font-size: 11.5px;
  font-weight: 600;
  color: #b91c1c;
  flex-shrink: 0;
}
.fcw-window-banner svg { flex-shrink: 0; color: #dc2626; }
.fcw-msg-search-bar {
  display: flex; align-items: center; gap: 4px;
  padding: 5px 8px; background: #fafafa;
  border-bottom: 1px solid var(--ink-10); flex-shrink: 0;
}
.fcw-msg-search-input {
  flex: 1; border: 1px solid var(--ink-15); border-radius: 6px;
  padding: 4px 8px; font-size: 11px; outline: none;
  background: var(--white); color: var(--ink-90);
}
.fcw-msg-search-input:focus { border-color: #BC0C24; }
.fcw-msg-search-count {
  font-size: 10px; color: var(--ink-50); white-space: nowrap; flex-shrink: 0;
}
.fcw-msg-search-nav,
.fcw-msg-search-close {
  background: none; border: none; cursor: pointer; padding: 3px 4px;
  color: var(--ink-50); border-radius: 4px; display: flex; align-items: center; flex-shrink: 0;
}
.fcw-msg-search-nav:hover,
.fcw-msg-search-close:hover { background: var(--ink-5); color: var(--ink-90); }
.msg-match-current { outline: 2px solid #f59e0b; outline-offset: -1px; border-radius: 8px; }
mark.msg-hl { background: #fef08a; color: #78350f; border-radius: 2px; padding: 0 1px; }

/* ── Macro Panel ──────────────────────────────────────── */
.fcw-btn--active { background: rgba(37,99,235,.15) !important; color: #2563eb !important; }

.fcw-macro-panel {
  display: flex; flex-direction: column;
  flex: 1; min-height: 0; overflow: hidden;
  border-top: 1px solid var(--ink-10);
}

.fcw-macro-head {
  display: flex; align-items: center; gap: 6px;
  padding: 8px 10px; border-bottom: 1px solid var(--ink-10);
  flex-shrink: 0;
}
.fcw-macro-search-wrap {
  display: flex; align-items: center; gap: 6px;
  flex: 1; background: var(--ink-02); border: 1px solid var(--ink-10);
  border-radius: 6px; padding: 0 8px;
}
.fcw-macro-search-wrap svg { color: var(--ink-40); flex-shrink: 0; }
.fcw-macro-search {
  flex: 1; border: none; outline: none; background: transparent;
  font-size: 12px; color: var(--ink-80); padding: 5px 0;
  font-family: inherit;
}
.fcw-macro-count {
  font-size: 10px; font-weight: 700; color: var(--ink-40);
  background: var(--ink-05); padding: 1px 6px; border-radius: 20px; flex-shrink: 0;
}

.fcw-macro-loading {
  flex: 1; display: flex; align-items: center; justify-content: center;
}

/* List */
.fcw-macro-list {
  flex: 1; overflow-y: auto; padding: 6px;
  display: flex; flex-direction: column; gap: 3px;
}
.fcw-macro-empty {
  text-align: center; color: var(--ink-40); font-size: 12px; padding: 20px;
}
.fcw-macro-item {
  display: flex; align-items: center; gap: 8px;
  padding: 8px 10px; border-radius: 6px;
  background: transparent; border: 1px solid transparent;
  cursor: pointer; text-align: left; font-family: inherit;
  width: 100%; transition: background var(--ease), border-color var(--ease);
}
.fcw-macro-item:hover {
  background: rgba(37,99,235,.07); border-color: rgba(37,99,235,.2);
}
.fcw-macro-item-info { flex: 1; min-width: 0; }
.fcw-macro-item-name {
  font-size: 12.5px; font-weight: 600; color: var(--ink-80);
  display: block; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.fcw-macro-item-count {
  font-size: 10.5px; color: var(--ink-40); margin-top: 1px; display: block;
}
.fcw-macro-item-run { color: #2563eb; flex-shrink: 0; opacity: .6; }
.fcw-macro-item:hover .fcw-macro-item-run { opacity: 1; }

/* Results */
.fcw-macro-results {
  flex: 1; overflow-y: auto; padding: 8px;
  display: flex; flex-direction: column; gap: 6px;
}
.fcw-macro-results-title {
  display: flex; align-items: center; gap: 8px;
  font-size: 12px; font-weight: 600; color: var(--ink-60);
  flex-shrink: 0;
}
.fcw-macro-back {
  display: inline-flex; align-items: center; gap: 3px;
  background: var(--ink-05); border: none; border-radius: 4px;
  padding: 3px 7px; font-size: 11px; font-weight: 600; color: var(--ink-60);
  cursor: pointer; font-family: inherit;
  transition: background var(--ease);
}
.fcw-macro-back:hover:not(:disabled) { background: var(--ink-10); }
.fcw-macro-back:disabled { opacity: .4; cursor: not-allowed; }

.fcw-macro-steps { display: flex; flex-direction: column; gap: 4px; }
.fcw-macro-step {
  display: flex; align-items: flex-start; gap: 8px;
  padding: 7px 9px; border-radius: 6px;
  background: var(--ink-02); border: 1px solid var(--ink-05);
}
.fcw-macro-step--ok  { background: rgba(22,163,74,.07);  border-color: rgba(22,163,74,.2); }
.fcw-macro-step--err { background: rgba(220,38,38,.07);  border-color: rgba(220,38,38,.2); }
.fcw-macro-step--pending { background: var(--ink-02); }

.fcw-macro-step-icon {
  width: 18px; height: 18px; border-radius: 50%; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
}
.fcw-macro-step--ok  .fcw-macro-step-icon { background: rgba(22,163,74,.15); color: #16a34a; }
.fcw-macro-step--err .fcw-macro-step-icon { background: rgba(220,38,38,.15); color: #dc2626; }

.fcw-macro-step-spin {
  width: 18px; height: 18px; border-radius: 50%; flex-shrink: 0;
  border: 2px solid var(--ink-10); border-top-color: #2563eb;
  animation: fcw-spin .7s linear infinite;
}
@keyframes fcw-spin { to { transform: rotate(360deg); } }

.fcw-macro-step-body { flex: 1; min-width: 0; }
.fcw-macro-step-label { font-size: 11.5px; font-weight: 600; color: var(--ink-80); }
.fcw-macro-step-err   { font-size: 10.5px; color: #dc2626; margin-top: 1px; }

.fcw-macro-summary {
  display: flex; align-items: center; gap: 6px;
  padding: 8px 10px; border-radius: 6px;
  font-size: 12px; font-weight: 600; flex-shrink: 0;
}
.fcw-macro-summary--ok   { background: rgba(22,163,74,.1);   border: 1px solid rgba(22,163,74,.25); color: #16a34a; }
.fcw-macro-summary--warn { background: rgba(245,158,11,.1);  border: 1px solid rgba(245,158,11,.3); color: #d97706; }
</style>
